import * as i0 from '@angular/core';
import { Injectable, Inject, Pipe, EventEmitter, Component, Input, Output, ViewChild, HostListener, Directive, NgModule } from '@angular/core';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import * as i1 from '@cudirect/shared-ui-library';
import { DictionaryPipe, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import * as i5 from '@angular/router';
import * as i6 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { KJUR, hextob64u } from 'jsrsasign';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as i6$1 from 'angular-code-input';
import { CodeInputModule } from 'angular-code-input';
import * as i7 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i1$1 from '@angular/platform-browser';
import * as i8 from 'ngx-mask';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

class OidLoginDataService {
    constructor(tokenService) {
        this.tokenService = tokenService;
        this.widgetExportData = new Subject();
        this.widgetStateExportData = new Subject();
        this.alertSet = new Subject();
        this.loginStep = null; // this is read by login.component to determine which view to display.
        this.redirectInProgress = false; // Used to prevent load spinner from stopping too early during an OIDC login
        this.newUser = null;
        this.skipMfaChoice = false;
        this.redirectFlow = false;
        this.passwordlessFlow = false;
        this.externalToken = null;
        // public externalProviderDiscoveryUrl: string;
        this.resend = false;
        this.discoverySub = new BehaviorSubject(null);
        this.oidReady = new BehaviorSubject(false);
        this.redirectButtonInfo = null;
        this.authType = 'default';
        this.loginInfo = this.setupLoginInfo();
    }
    clearData() {
        this.loginInfo = this.setupLoginInfo();
        this.loginStep = null;
        this.alert = null;
        this.userMfaPreferences = null;
    }
    /**
     * Sets one or more parameters in the login info object.
     * @param param a subset of the properties to change.
     */
    setLoginInfo(param) {
        this.loginInfo = { ...this.loginInfo, ...param };
    }
    /**
     * Retrieves either the entire login info object or a single parameter from it.
     * @param param Optional parameter to retrieve
     * @returns Either the whole object or the parameter value.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getLoginInfo(key) {
        if (!key) {
            return this.loginInfo;
        }
        else {
            return this.loginInfo[key];
        }
    }
    /**
     * Method to export token and data to the host application. Always sends the token, email address, and mfa status.
     * @param msg Optional message to send. Will be included in the statusMsg property if present.
     */
    sendDataToHost(msg = '', redirect = false, provider = '') {
        this.externalToken = this.externalToken;
        const exportData = {
            token: this.tokenService.jwt || null,
            statusMsg: msg,
            loginRedirect: redirect,
            provider
        };
        this.widgetExportData.next(exportData);
    }
    /**
     * Method to export other data types to host application.
     * @param data Data to send to host
     */
    sendStateToHost(data) {
        this.widgetStateExportData.next(data);
    }
    // The initial state of the login info object.
    setupLoginInfo() {
        return {
            mfaMethod: null,
            isMfaRequired: false,
            widgetMode: 'login',
            rememberUsername: false,
            availMfaMethod: null,
            authProviderId: null,
            authProviderArray: [],
            userId: null,
            method: null,
            loginMode: 'email',
            correlationId: null,
            verifyOnly: false
        };
    }
    /**
     * Method to set an alert to be displayed on the login page.
     * @param data The alert to set.
     */
    setupAlert(data) {
        this.alert = data;
        if (this.alert) {
            this.alertSet.next(this.alert);
        }
    }
    /**
     * Method to get the alert if there is one to display. Will then clear the alert.
     */
    getAlert() {
        let alertToSend;
        if (this.alert) {
            alertToSend = { ...this.alert };
            this.alert = null;
        }
        return alertToSend || null;
    }
    /**
     * Helper method to determine if a url is internal/external
     * @param url The url to evaluate
     * @returns boolean
     */
    isExternalURL(url) {
        if (!url)
            return false;
        const parser = new RegExp('^(?:http(s)?://)+'); // starts with http(s): //
        return parser.test(url.toLowerCase());
    }
    /**
     * Sets up the OIDC configuration object.
     * @param config the specific configuration object to set.
     */
    setOidConfig(config) {
        this.oidConfig = config;
        this.passwordlessFlow = this.oidConfig.supportedLoginTypes.includes('passwordless');
        this.redirectFlow = this.oidConfig.supportedLoginTypes.includes('redirect');
        this.redirectButtonInfo = [{ data: this.oidConfig.redirect }];
    }
    /**
     * Getter for the oid config
     * @returns the config object
     */
    getOidConfig() {
        return this.oidConfig;
    }
    /**
     * Sets the discovery doc and emits to observers
     * @param doc The contents of the discovery doc
     */
    setDiscoveryDoc(doc) {
        this.discoveryDoc = doc;
        this.discoverySub.next(this.discoveryDoc);
    }
    /**
     * Getter for the discovery doc
     * @returns the discovery doc
     */
    getDiscoveryDoc() {
        return this.discoverySub.asObservable();
    }
    /**
     * Observable to determine if the OIDC config is ready
     * @returns boolean
     */
    isOidReady() {
        return this.oidReady.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidLoginDataService, deps: [{ token: i1.TokenService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidLoginDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidLoginDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.TokenService }] });

var widgetName = "@cudirect-widgets/ea-login";
var widgetVersion = "18.0.5";
var WidgetVersionInfo = {
	widgetName: widgetName,
	widgetVersion: widgetVersion
};

/**
 * Called from a form to disallow spaces if typing in the email entry.
 * @param event the keyboard event
 * @returns false to prevent space
 */
const checkForWhitespace = (event) => {
    if (event.code === 'Space') {
        return false;
    }
};
/**
 * Tests the user input to determine if it is a US Phone Number or international number
 * @param id The user id
 * @returns boolean true if phone number, false if user name
 */
const isPhoneNumber = (id) => {
    const phoneRegEx = 
    // eslint-disable-next-line max-len
    /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?(\d{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
    const fixed = fixPhoneNumber(id);
    if (phoneRegEx.test(id) && fixed.length === 10)
        return true; // Standard American Phone number
    // Check if international number
    const charRegExp = /[a-zA-Z]/g;
    if (charRegExp.test(id))
        return false; // Contains letters, so not a phone number
    // We think it's all digits, so check length
    return fixed.length > 10 && fixed.length <= 16; // International number length
};
/**
 * Helper method to strip a phone number of any formatting.
 * @param phone The number to strip
 */
const fixPhoneNumber = (phone) => {
    let fixed = phone.replace(/\D/g, '');
    if (fixed.charAt(0) === '1') {
        fixed = fixed.substring(1);
    }
    return fixed;
};

class EaWidgetContextService {
    constructor() {
        this.widgetContext$ = new BehaviorSubject(null);
    }
    /**
     * Set up the widget context data, supporting both angular and agnostic mode
     * @param widgetContext - the inital context passed into the widget
     * @returns the widget context data
     */
    initWidgetContext(widgetContext) {
        if (typeof widgetContext === 'string') {
            this.widgetContext = JSON.parse(widgetContext);
            this.widgetContext.isAgnosticMode = true;
        }
        else {
            this.widgetContext = widgetContext;
            this.widgetContext.isAgnosticMode = false;
        }
        this.widgetContext$.next(this.widgetContext);
        return this.widgetContext;
    }
    /**
     * Method to retreive the current widget context
     */
    getWidgetContext() {
        return this.widgetContext$.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaWidgetContextService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaWidgetContextService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaWidgetContextService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class JsrsAsignReducedService {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    generateCodeChallenge(codeVerifier) {
        const hash = KJUR.crypto.Util.hashString(codeVerifier, 'sha256');
        const testData = hextob64u(hash);
        return testData;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    generateAtHash(accessToken, sha) {
        const hash = KJUR.crypto.Util.hashString(accessToken, sha);
        const first128bits = hash.substr(0, hash.length / 2);
        const testData = hextob64u(first128bits);
        return testData;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: JsrsAsignReducedService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: JsrsAsignReducedService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: JsrsAsignReducedService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class OIDCService {
    constructor(jsrsAsignReducedService, doc, http, loginService, widgetContextService) {
        this.jsrsAsignReducedService = jsrsAsignReducedService;
        this.doc = doc;
        this.http = http;
        this.loginService = loginService;
        this.widgetContextService = widgetContextService;
        // Holder for subscriptions for ease of unsub
        this.subscriptions = new Subscription();
        // Set the widget context data. Lets evaluate whether this needs to use the subscription
        const contextSub = this.widgetContextService.getWidgetContext().subscribe((res) => {
            this.widgetContextData = res;
        });
        this.subscriptions.add(contextSub);
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    /**
     * Creates a random string of characters
     * @param requiredLength How long the string should be.
     * @returns the random string
     */
    createRandom(requiredLength) {
        if (requiredLength <= 0) {
            return '';
        }
        if (requiredLength > 0 && requiredLength < 7) {
            requiredLength = 10;
        }
        const length = requiredLength - 6;
        const arr = new Uint8Array(Math.floor((length || length) / 2));
        if (this.getCrypto()) {
            this.getCrypto().getRandomValues(arr);
        }
        return Array.from(arr, this.toHex).join('') + this.randomString(7);
    }
    /**
     * Helper function for the createRandom method
     * @param length
     * @returns string
     */
    randomString(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const values = new Uint32Array(length);
        if (this.getCrypto()) {
            this.getCrypto().getRandomValues(values);
            for (let i = 0; i < length; i++) {
                result += characters[values[i] % characters.length];
            }
        }
        return result;
    }
    /**
     * Helper function for browser compatibility. Used by createRandom.
     * @returns crypto object
     */
    getCrypto() {
        // support for IE,  (window.crypto || window.msCrypto)
        return this.doc.defaultView.crypto || this.doc.defaultView.msCrypto;
    }
    /**
     * Helper method to convert dec to hex. Used by createRandom.
     * @param dec
     * @returns hex string
     */
    toHex(dec) {
        return ('0' + dec.toString(16)).substr(-2);
    }
    /**
     * Creates the nonce. 40 random chars.
     * @returns the random nonce.
     */
    createNonce() {
        const nonce = this.createRandom(40);
        return nonce;
    }
    /**
     * Creates the state. 40 random chars.
     * @returns the random state.
     */
    createAuthStateControl() {
        let state = this.createRandom(40);
        return state;
    }
    /**
     * Creates a random code verifier (67 random chars). This will be encoded and used in the oidc request url.
     * @returns the random code verifier.
     */
    createCodeVerifier() {
        const codeVerifier = this.createRandom(67);
        return codeVerifier;
    }
    /**
     * Creates the URL and all the query params for a redirect out to an OIDC provider. Populates the nonce and state
     * as well as the code verifier and challenge.
     * @param url the base url
     * @param paramsObj an object of all the query params to be added to the url.
     * @returns the fully formed url
     */
    createUrlCodeFlowAuthorize(btnInfo, email, widgetConfig, isRememberMe) {
        const data = btnInfo.data;
        const paramsObj = data.params;
        const state = this.createAuthStateControl();
        const nonce = this.createNonce();
        // code_challenge with "S256"
        const code_verifier = this.createCodeVerifier(); // Random 67
        const codeChallenge = this.jsrsAsignReducedService.generateCodeChallenge(code_verifier); // encodes the code verifier
        paramsObj.code_challenge = codeChallenge;
        paramsObj.nonce = nonce;
        paramsObj.state = state;
        // Are we remembering the user?
        // tslint:disable-next-line: no-null-keyword
        // Lets save the email in session storage if an email is present and the user has selected to remember option.
        const rememberMeEmail = email && isRememberMe ? email : null;
        const oidcStorage = {
            ...data,
            code_verifier,
            providerId: btnInfo.id,
            rememberMeEmail,
            widgetConfig
        };
        // TODO: need to add in email and if they checked the remember button
        sessionStorage.setItem('ea-oidc', JSON.stringify(oidcStorage));
        // Prefill the email with the one they typed.
        // This param documented here - https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-protocols-oidc
        // Wrapped in a conditional in case CUID fails to send email, don't send NULL to oidc provider.
        if (email) {
            paramsObj.login_hint = email;
        }
        return this.buildUrl(data.authUri, paramsObj);
    }
    /**
     * Builds a complete url. Takes the base url and then iterates thru the params obj adding each one as a query parameter
     * @param url The base url
     * @param params Key/Value pairs for each query parameter
     * @returns the fully formed, encoded url
     */
    buildUrl(url, params) {
        let completeUrl = url + '?';
        let first = true;
        for (const [key, value] of Object.entries(params)) {
            if (first) {
                first = false;
            }
            else {
                completeUrl += '&';
            }
            completeUrl += encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return completeUrl;
    }
    /**
     * Handle the code from the OIDC redirect.
     * @param code the code from the URL query params
     * @param oidcObj The oidc info that was stored before the redirect
     * @returns async promise
     */
    async handleOIDCode(code, oidcObj) {
        const bodyParams = {
            client_id: oidcObj.params.client_id,
            grant_type: 'authorization_code',
            redirect_uri: oidcObj.params.redirect_uri,
            code,
            code_verifier: oidcObj.code_verifier,
            nonce: oidcObj.params.nonce
        };
        const headers = new HttpHeaders({
            'content-type': 'application/x-www-form-urlencoded'
        });
        const body = Object.keys(bodyParams)
            .map(function (k) {
            return encodeURIComponent(k) + '=' + encodeURIComponent(bodyParams[k]);
        })
            .join('&');
        const asyncResponse = await this.http.post(oidcObj.tokenUri, body, { headers }).toPromise();
        return asyncResponse;
    }
    handleAuth0ButtonClick(btnInfo, discDoc, testingMode = false) {
        const loginInfo = this.loginService.getLoginInfo();
        const userEmail = loginInfo.userId;
        const isRememberMe = loginInfo.rememberUsername;
        btnInfo.data.authUri = discDoc.authorization_endpoint;
        btnInfo.data.tokenUri = discDoc.token_endpoint;
        btnInfo.data.params.redirect_uri = window.location.href;
        btnInfo.type = 'auth0';
        const formedUrl = this.createUrlCodeFlowAuthorize(btnInfo, userEmail, this.widgetContextData, isRememberMe);
        if (!testingMode) {
            window.location.href = formedUrl;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OIDCService, deps: [{ token: JsrsAsignReducedService }, { token: DOCUMENT }, { token: i2.HttpClient }, { token: OidLoginDataService }, { token: EaWidgetContextService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OIDCService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OIDCService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: JsrsAsignReducedService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i2.HttpClient }, { type: OidLoginDataService }, { type: EaWidgetContextService }] });

const timeoutConfig = {
    view: 'chooseMfa',
    sessionTimeoutInMins: 10,
    redirect: 'login'
};

class EaTimeoutService {
    constructor(loginService) {
        this.loginService = loginService;
        this._config = timeoutConfig; // Sets all the messaging as well as the timeout duration.
    }
    /**
     * Method to start the timer. If timer expires, user will be set back to login.
     * @param minutes Optional override of default time.
     */
    startWidgetTimeout(minutes = this._config.sessionTimeoutInMins) {
        if (this.userActivity) {
            clearTimeout(this.userActivity);
        }
        const intervalInMilliseconds = minutes * 60 * 1000;
        this.userActivity = window.setTimeout(() => {
            this.loginService.sendStateToHost({
                event: 'timeout',
                code: 'sendToLogin',
                success: false,
                data: { timeoutOnPage: this.loginService.loginStep }
            });
            this.loginService.setLoginInfo({ widgetMode: 'login' });
            this.loginService.loginStep = 'timeout';
        }, intervalInMilliseconds);
    }
    stopWidgetTimeout() {
        clearTimeout(this.userActivity);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaTimeoutService, deps: [{ token: OidLoginDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaTimeoutService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaTimeoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: OidLoginDataService }] });

class EllipsesPipe {
    transform(str, len) {
        if (!str) {
            return '';
        }
        if (str.length < len) {
            return str;
        }
        else {
            return str.slice(0, len) + '...';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EllipsesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: EllipsesPipe, name: "ellipses" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EllipsesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ellipses'
                }]
        }] });

class OidVerifyCodeComponent {
    constructor(widgetContextService, oidLoginService, tokenService, timeoutService, dictionaryPipe) {
        this.widgetContextService = widgetContextService;
        this.oidLoginService = oidLoginService;
        this.tokenService = tokenService;
        this.timeoutService = timeoutService;
        this.dictionaryPipe = dictionaryPipe;
        this.shouldShowAlert = false;
        this.loadSpinner = new EventEmitter();
        this.codeResent = false;
        this.maskedId = '';
        this.subtitleSuffix = '';
        this.verifyOnly = false;
        this.verifySubject = new Subject();
        this.ngUnsubscribe = new Subject();
        this.isDxMode = false;
        this.mfaData = {
            code: '',
            rememberDevice: false
        };
    }
    /**
     * Init verify code component
     */
    ngOnInit() {
        // Set the widget conext data. Lets evaluate whether this needs to use the subscription
        this.widgetContextData = this.widgetContextService.widgetContext;
        // Start the timeout counter
        this.timeoutService.startWidgetTimeout();
        const userId = this.oidLoginService.getLoginInfo('userId');
        const method = this.oidLoginService.getLoginInfo('method')?.toString().toLowerCase();
        this.isDxMode = (this.widgetContextData.oid.dxMode || false) && method === 'email_code';
        if (method === 'email_code') {
            // TODO: Subtitle suffix is not currently in the dictionary as the dictionary is not loaded in time
            // to display it in the case of a deep link. Investigate workarounds.
            this.subtitleSuffix = 'email.';
            const parts = userId.split('@');
            const firstLetter = parts[0].charAt(0);
            const suffix = parts[0].substring(parts[0].length, parts[0].length - 2);
            this.maskedId = firstLetter;
            for (let i = 0; i < parts[0].length - 3; i++) {
                this.maskedId += '*';
            }
            this.maskedId += suffix + '@' + parts[1];
        }
        else if (method === 'phone') {
            this.subtitleSuffix = 'phone.';
            if (userId.length === 10) {
                this.maskedId = '(' + userId.substring(0, 3) + ') ***-**' + userId.substring(8, 10);
            }
            else {
                // International phone number
                const numOfStars = userId.length - 6;
                this.maskedId = userId.substring(0, 3);
                for (let i = 0; i < numOfStars; i++) {
                    this.maskedId += '*';
                }
                this.maskedId += userId.substring(userId.length - 3, userId.length);
            }
        }
        // TODO: Need logic to determine if both methods are valid or not. The last api call needs to tell me that I think?
        // Or do we let them pick? Masked? Completely obfuscated?
        // this.loginService.skipMfaChoice = true;
        // TODO: Currently, the api does not distinguish between expired codes and incorrect codes. If it does,
        // we can use the response to modify this block of text.
        this.codeErrorText = 'Invalid code. Please try again';
        this.verifyOnly = this.oidLoginService.getLoginInfo('verifyOnly');
        this.verifySubject.pipe(debounceTime(300), takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.verify();
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    ngAfterViewInit() {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        for (let i = 0; i < this.codeInput['inputs'].length; i++) {
            // ensure left padding is 0 on wrapper span
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.codeInput['inputs'][i].parentNode.classList.add('bl-padding-left--0');
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.codeInput['inputs'][i].id = 'code' + (i + 1);
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.codeInput['inputs'][i].setAttribute('aria-label', 'Digit ' + (i + 1) + ' of 6 digit verification code');
            this.codeInput['inputs'][i].setAttribute('aria-describedby', 'code_error');
        }
    }
    /**
     * Function to reset codeNotMatch. Ignores enter press and complete MFA codes.
     * @param event - Keyup event
     */
    reEnterCode(event) {
        if (event.keyCode !== 13 && this.mfaData.code.length < 6) {
            this.codeNotMatch = false;
        }
    }
    /**
     * Function to resend email code to legacy users who have no phone number on file.
     * @param pref - either 'phone' or 'email' depending ont he user's choice.
     */
    resendCode() {
        this.oidLoginService.resend = true;
        this.oidLoginService.loginStep = 'oid-start';
    }
    /**
     * Starts the login process over to allow user to choose new email/phone
     */
    startOver() {
        this.oidLoginService.resend = false;
        this.oidLoginService.loginStep = 'oid-start';
    }
    /**
     * Method to verify the MFA code using CUID Shared UI.
     */
    verify() {
        this.oidLoginService.sendStateToHost({
            event: 'Login',
            code: 'MFA Verify Started',
            success: false,
            data: null
        });
        this.loadSpinner.emit({
            loading: true,
            msg: this.dictionaryPipe.transform('ea-authenticate-oid-verify-code/spinnerMsg')
        });
        const loginInfo = this.oidLoginService.getLoginInfo();
        const request = {
            userId: loginInfo.userId,
            passwordless_code: this.mfaData.code,
            method: loginInfo.method
        };
        this.tokenService.verifyPasswordless(request.userId, request.method, request.passwordless_code).then((res) => {
            if (res.isSuccess) {
                if (this.oidLoginService.getLoginInfo('rememberUsername')) {
                    localStorage.setItem('ea-login-id', loginInfo.userId);
                }
                this.tokenService.updateToken(res.data);
                this.codeNotMatch = false;
                this.oidLoginService.sendStateToHost({
                    event: 'Login',
                    code: 'MFAVerified',
                    success: true,
                    data: 'MFA completed. User logged in.'
                });
                this.oidLoginService.sendDataToHost('Authenticated');
            }
            else {
                this.loadSpinner.emit({
                    loading: false
                });
                this.mfaData.code = '';
                this.codeInput.reset();
                this.codeNotMatch = true;
            }
        });
    }
    /**
     * Fired every time the code input changes.
     * @param e the current code entry
     */
    onCodeChanged(e) {
        this.mfaData.code = e;
    }
    /**
     * Fires once the code input is complete.
     * @param e the current code entry
     */
    onCodeCompleted(e) {
        this.mfaData.code = e;
        this.verifySubject.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidVerifyCodeComponent, deps: [{ token: EaWidgetContextService }, { token: OidLoginDataService }, { token: i1.TokenService }, { token: EaTimeoutService }, { token: i1.DictionaryPipe }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: OidVerifyCodeComponent, selector: "oid-verify", inputs: { shouldShowAlert: "shouldShowAlert" }, outputs: { loadSpinner: "loadSpinner" }, host: { listeners: { "window: keyup": "reEnterCode($event)" } }, viewQueries: [{ propertyName: "codeInput", first: true, predicate: ["codeInput"], descendants: true }, { propertyName: "element", first: true, predicate: ["verifyBtn"], descendants: true }], ngImport: i0, template: "<h1 class=\"bl-font-weight--900 bl-font-color--black bl-font-size--28\">\n  {{ 'ea-authenticate-oid-verify-code/verifyIdentity' | dictionary }}\n</h1>\n\n<p *ngIf=\"!isDxMode\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n  {{ 'ea-authenticate-oid-verify-code/subtitle' | dictionary }}{{ subtitleSuffix }}\n</p>\n\n<div class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--steel bl-margin-top--8 bl-margin-bottom-24\">\n  <span *ngIf=\"!isDxMode\" class=\"bl-font-weight--800 bl-font-size--14\">{{ 'ea-authenticate-oid-verify-code/codeSent' | dictionary }}:</span>\n  <span *ngIf=\"isDxMode\" class=\"bl-font-weight--800 bl-font-size--14\">{{\n    'ea-authenticate-oid-verify-code/dxModeCodeSent' | dictionary\n  }}</span>\n\n  <div class=\"bl-display--flex bl-justify-content--space-between\">\n    <span *ngIf=\"!isDxMode\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-display--block\">\n      {{ maskedId | ellipses: 25 }}\n    </span>\n    <div *ngIf=\"isDxMode\"></div>\n    <!-- Just for spacing -->\n    <button\n      *ngIf=\"!verifyOnly && !isDxMode\"\n      id=\"start-over\"\n      (click)=\"startOver()\"\n      tabindex=\"0\"\n      class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/change' | dictionary }}\n    </button>\n    <button\n      *ngIf=\"!verifyOnly && isDxMode\"\n      id=\"start-over\"\n      (click)=\"startOver()\"\n      tabindex=\"0\"\n      class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/changeDxMode' | dictionary }}\n    </button>\n  </div>\n</div>\n\n<div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top--24\">\n  <label for=\"code\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n    {{ 'ea-authenticate-oid-verify-code/codeLabel' | dictionary }}\n  </label>\n\n  <code-input\n    #codeInput\n    id=\"ea-code-input\"\n    [isCodeHidden]=\"false\"\n    [codeLength]=\"6\"\n    [isFocusingOnLastByClickIfFilled]=\"false\"\n    initialFocusField=\"0\"\n    (codeChanged)=\"onCodeChanged($event)\"\n    (codeCompleted)=\"onCodeCompleted($event)\"\n  >\n  </code-input>\n  <span id=\"code_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"codeNotMatch\"> {{ codeErrorText }} </span>\n</div>\n\n<div class=\"bl-margin-y--24 bl-display--flex bl-justify-content--space-around\">\n  <div>\n    <span> {{ 'ea-authenticate-oid-verify-code/didnotreceive' | dictionary }} </span>\n    <a\n      id=\"resend-code-btn\"\n      tabindex=\"0\"\n      class=\"bl-font-weight--400 bl-text-decoration--none bl-hover-text-decoration--none\"\n      (click)=\"resendCode()\"\n      (keyup.enter)=\"resendCode()\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/resendSameMethod' | dictionary }}\n    </a>\n  </div>\n</div>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}code-input#ea-code-input{max-width:464px;min-width:240px;margin-left:auto;margin-right:auto;color:var(--neutral-font-color-steel);font-size:28px;font-weight:bolder;--item-spacing: 4px;--item-height: 56px;--item-border: 1px solid var(--input-border-color);--item-border-bottom: 1px solid var(--input-border-color);--item-border-focused: 3px solid #006dbd;--item-border-bottom-focused: 3px solid #006dbd;--item-shadow-focused: 0 0 4px 0 rgba(0, 109, 189, .25);--item-border-radius: 4px;--item-font-weight: 900}@media (min-width: 576px){code-input#ea-code-input{font-size:32px;--item-spacing: 16px;--item-height: 72px}}\n"], dependencies: [{ kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i6$1.CodeInputComponent, selector: "code-input", inputs: ["codeLength", "inputType", "inputMode", "initialFocusField", "isNonDigitsCode", "isCharsCode", "isCodeHidden", "isPrevFocusableAfterClearing", "isFocusingOnLastByClickIfFilled", "code", "disabled", "autocapitalize"], outputs: ["codeChanged", "codeCompleted"] }, { kind: "pipe", type: i1.DictionaryPipe, name: "dictionary" }, { kind: "pipe", type: EllipsesPipe, name: "ellipses" }], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidVerifyCodeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'oid-verify', template: "<h1 class=\"bl-font-weight--900 bl-font-color--black bl-font-size--28\">\n  {{ 'ea-authenticate-oid-verify-code/verifyIdentity' | dictionary }}\n</h1>\n\n<p *ngIf=\"!isDxMode\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n  {{ 'ea-authenticate-oid-verify-code/subtitle' | dictionary }}{{ subtitleSuffix }}\n</p>\n\n<div class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--steel bl-margin-top--8 bl-margin-bottom-24\">\n  <span *ngIf=\"!isDxMode\" class=\"bl-font-weight--800 bl-font-size--14\">{{ 'ea-authenticate-oid-verify-code/codeSent' | dictionary }}:</span>\n  <span *ngIf=\"isDxMode\" class=\"bl-font-weight--800 bl-font-size--14\">{{\n    'ea-authenticate-oid-verify-code/dxModeCodeSent' | dictionary\n  }}</span>\n\n  <div class=\"bl-display--flex bl-justify-content--space-between\">\n    <span *ngIf=\"!isDxMode\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-display--block\">\n      {{ maskedId | ellipses: 25 }}\n    </span>\n    <div *ngIf=\"isDxMode\"></div>\n    <!-- Just for spacing -->\n    <button\n      *ngIf=\"!verifyOnly && !isDxMode\"\n      id=\"start-over\"\n      (click)=\"startOver()\"\n      tabindex=\"0\"\n      class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/change' | dictionary }}\n    </button>\n    <button\n      *ngIf=\"!verifyOnly && isDxMode\"\n      id=\"start-over\"\n      (click)=\"startOver()\"\n      tabindex=\"0\"\n      class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/changeDxMode' | dictionary }}\n    </button>\n  </div>\n</div>\n\n<div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top--24\">\n  <label for=\"code\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n    {{ 'ea-authenticate-oid-verify-code/codeLabel' | dictionary }}\n  </label>\n\n  <code-input\n    #codeInput\n    id=\"ea-code-input\"\n    [isCodeHidden]=\"false\"\n    [codeLength]=\"6\"\n    [isFocusingOnLastByClickIfFilled]=\"false\"\n    initialFocusField=\"0\"\n    (codeChanged)=\"onCodeChanged($event)\"\n    (codeCompleted)=\"onCodeCompleted($event)\"\n  >\n  </code-input>\n  <span id=\"code_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"codeNotMatch\"> {{ codeErrorText }} </span>\n</div>\n\n<div class=\"bl-margin-y--24 bl-display--flex bl-justify-content--space-around\">\n  <div>\n    <span> {{ 'ea-authenticate-oid-verify-code/didnotreceive' | dictionary }} </span>\n    <a\n      id=\"resend-code-btn\"\n      tabindex=\"0\"\n      class=\"bl-font-weight--400 bl-text-decoration--none bl-hover-text-decoration--none\"\n      (click)=\"resendCode()\"\n      (keyup.enter)=\"resendCode()\"\n    >\n      {{ 'ea-authenticate-oid-verify-code/resendSameMethod' | dictionary }}\n    </a>\n  </div>\n</div>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}code-input#ea-code-input{max-width:464px;min-width:240px;margin-left:auto;margin-right:auto;color:var(--neutral-font-color-steel);font-size:28px;font-weight:bolder;--item-spacing: 4px;--item-height: 56px;--item-border: 1px solid var(--input-border-color);--item-border-bottom: 1px solid var(--input-border-color);--item-border-focused: 3px solid #006dbd;--item-border-bottom-focused: 3px solid #006dbd;--item-shadow-focused: 0 0 4px 0 rgba(0, 109, 189, .25);--item-border-radius: 4px;--item-font-weight: 900}@media (min-width: 576px){code-input#ea-code-input{font-size:32px;--item-spacing: 16px;--item-height: 72px}}\n"] }]
        }], ctorParameters: () => [{ type: EaWidgetContextService }, { type: OidLoginDataService }, { type: i1.TokenService }, { type: EaTimeoutService }, { type: i1.DictionaryPipe }], propDecorators: { shouldShowAlert: [{
                type: Input
            }], loadSpinner: [{
                type: Output
            }], codeInput: [{
                type: ViewChild,
                args: ['codeInput']
            }], element: [{
                type: ViewChild,
                args: ['verifyBtn']
            }], reEnterCode: [{
                type: HostListener,
                args: ['window: keyup', ['$event']]
            }] } });

const emailRegEx = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]{2,64}\\.[a-zA-Z]{2,64}$';
const emailAsRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]{2,64}\.[a-zA-Z]{2,64}$/;

class LoginBtnComponent {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
        this.btnId = 'redirect-btn';
        this.clickAction = new EventEmitter();
    }
    ngOnInit() {
        this.logoImgPath = this.sanitizer.bypassSecurityTrustResourceUrl(this.providerData.data.buttonLogo);
    }
    handleClick() {
        this.clickAction.emit(this.providerData);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoginBtnComponent, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: LoginBtnComponent, selector: "ea-login-btn", inputs: { providerData: "providerData", btnId: "btnId" }, outputs: { clickAction: "clickAction" }, ngImport: i0, template: "<div>\n  <button\n    type=\"button\"\n    (click)=\"handleClick()\"\n    class=\"bl-button bl-button-reverse bl-margin-y--8 bl-padding--8 bl-width--100 login-btn\"\n    [id]=\"btnId\"\n  >\n    <div class=\"bl-display--flex\">\n      <img alt=\"Logo for {{ providerData.data.buttonLabel }}\" [src]=\"logoImgPath\" class=\"provider-logo bl-margin-y--auto\" />\n      <span class=\"bl-align-self--center bl-width--100 bl-word-break--break-word bl-font-weight--700 bl-ellipsis bl-padding-left--8\">\n        {{ providerData.data.buttonLabel }}\n      </span>\n    </div>\n  </button>\n</div>\n", styles: [".login-btn{border-width:2px}.provider-logo{max-width:64px;max-height:32px}\n"], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoginBtnComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ea-login-btn', template: "<div>\n  <button\n    type=\"button\"\n    (click)=\"handleClick()\"\n    class=\"bl-button bl-button-reverse bl-margin-y--8 bl-padding--8 bl-width--100 login-btn\"\n    [id]=\"btnId\"\n  >\n    <div class=\"bl-display--flex\">\n      <img alt=\"Logo for {{ providerData.data.buttonLabel }}\" [src]=\"logoImgPath\" class=\"provider-logo bl-margin-y--auto\" />\n      <span class=\"bl-align-self--center bl-width--100 bl-word-break--break-word bl-font-weight--700 bl-ellipsis bl-padding-left--8\">\n        {{ providerData.data.buttonLabel }}\n      </span>\n    </div>\n  </button>\n</div>\n", styles: [".login-btn{border-width:2px}.provider-logo{max-width:64px;max-height:32px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }], propDecorators: { providerData: [{
                type: Input
            }], btnId: [{
                type: Input
            }], clickAction: [{
                type: Output
            }] } });

class SeparatorComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SeparatorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: SeparatorComponent, selector: "ea-separator", inputs: { textContent: "textContent" }, ngImport: i0, template: "<div class=\"separator\">{{ textContent }}</div>\n", styles: [":host{display:block}.separator{display:flex;align-items:center;text-align:center}.separator:before,.separator:after{content:\"\";flex:1;border-bottom:2px solid #dde5eb}.separator:not(:empty):before{margin-right:.5em}.separator:not(:empty):after{margin-left:.5em}\n"], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SeparatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ea-separator', template: "<div class=\"separator\">{{ textContent }}</div>\n", styles: [":host{display:block}.separator{display:flex;align-items:center;text-align:center}.separator:before,.separator:after{content:\"\";flex:1;border-bottom:2px solid #dde5eb}.separator:not(:empty):before{margin-right:.5em}.separator:not(:empty):after{margin-left:.5em}\n"] }]
        }], ctorParameters: () => [], propDecorators: { textContent: [{
                type: Input
            }] } });

class OidLoginComponent {
    /**
     * Function listening for and handling an Enter key press with full MFA code entered.
     * @param event - keydown event
     */
    enter(event) {
        if (event.keyCode === 13 && this.isIDValid) {
            event.preventDefault();
            this.startPasswordless();
        }
    }
    constructor(tokenService, widgetContextService, oidLoginService, timeoutService, dictionaryPipe, cd, focusService, oidcService) {
        this.tokenService = tokenService;
        this.widgetContextService = widgetContextService;
        this.oidLoginService = oidLoginService;
        this.timeoutService = timeoutService;
        this.dictionaryPipe = dictionaryPipe;
        this.cd = cd;
        this.focusService = focusService;
        this.oidcService = oidcService;
        this.shouldShowAlert = false;
        this.loadSpinner = new EventEmitter();
        /**
         * UI Switch to determine if pw field is shown.
         */
        this.lockUsernameField = false;
        this.loginData = {
            userId: '',
            method: '',
            rememberUsername: false
        };
        /**
         * UI Helpers
         */
        this.showAlert = false;
        // public sub: Subscription;
        this.preventAlertDismissal = false;
        this.checkForWhitespace = checkForWhitespace;
        this.isPhoneNumber = isPhoneNumber;
        this.fixPhoneNumber = fixPhoneNumber;
        this.emailRegEx = emailAsRegex;
        this.shouldHideContinue = false;
        this.showSubtitle = false;
        this.isAutoLogin = false;
        this.isIDValid = false;
        this.emailNotVerified = false;
        this.forceErrorState = false;
        this.userIdInputMode = 'text';
        this.subscriptions = new Subscription();
    }
    /**
     * Init the ea login welcome component
     */
    ngOnInit() {
        // Set the widget context data.
        if (!this.widgetContextData && this.widgetContextService.widgetContext) {
            this.widgetContextData = this.widgetContextService.widgetContext;
        }
        this.loadSpinner.emit({
            loading: true,
            msg: 'Retrieving Login Information'
        });
        this.cd.detectChanges();
        // Stop the timeout counter
        this.timeoutService.stopWidgetTimeout();
        this.isAutoLogin = this.oidLoginService.getLoginInfo('autoLogin');
        this.setLabels();
        if (this.isAutoLogin) {
            setTimeout(() => {
                this.loadSpinner.emit({
                    loading: true
                });
            }, 0);
            this.loginData.userId = this.oidLoginService.autoLoginInfo.email;
            if (this.loginData.userId) {
                this.handleIDInput(this.loginData.userId);
                this.oidLoginService.setLoginInfo({ availMfaMethod: 'email' });
                this.oidLoginService.userMfaPreferences = {
                    phone: null
                };
                this.startPasswordless();
            }
        }
        if (this.oidLoginService.loginStep === 'timeout') {
            this.setAlert({
                type: 'warning',
                heading: this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/timeout/title'),
                message: this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/timeout/detail')
            });
            this.oidLoginService.loginStep = 'oid-start';
        }
        this.showSubtitle = this.dictionaryPipe.transform('ea-authenticate-oid-login/subtitle').length > 0;
        this.timeoutService.stopWidgetTimeout();
        // This is how other pages set alerts. If they set one, display it.
        const alertToSet = this.oidLoginService.getAlert();
        if (alertToSet) {
            this.setAlert(alertToSet);
        }
        if (this.oidLoginService.resend) {
            this.loadSpinner.emit({
                loading: true
            });
            this.loginData.userId = this.oidLoginService.getLoginInfo('userId');
            this.loginData.method = this.oidLoginService.getLoginInfo('method');
            this.handleIDInput(this.loginData.userId);
            this.startPasswordless();
            this.oidLoginService.resend = false;
        }
        const discoverySub = this.oidLoginService.getDiscoveryDoc().subscribe((discDoc) => {
            this.discoveryDoc = discDoc;
        });
        this.subscriptions.add(discoverySub);
        const readySub = this.oidLoginService.isOidReady().subscribe((ready) => {
            if (ready && !this.isAutoLogin) {
                this.loadSpinner.emit({
                    loading: false
                });
                this.cd.detectChanges();
            }
        });
        this.subscriptions.add(readySub);
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    // TODO: Evaluate if this is needed
    ngAfterViewInit() {
        const rememberedId = localStorage.getItem('ea-login-id');
        const rememberedEmail = this.oidLoginService.getLoginInfo('userId');
        if (rememberedId && !this.isAutoLogin) {
            this.loginData.rememberUsername = true;
            this.loginData.userId = rememberedId;
            this.handleIDInput(this.loginData.userId);
            this.oidLoginService.setLoginInfo({ rememberUsername: true });
        }
        else if (rememberedEmail) {
            this.loginData.userId = rememberedEmail;
            this.handleIDInput(this.loginData.userId);
        }
    }
    /**
     * Method to trim whitespace from the email entry
     * @param email the email address from the form
     */
    handleIDInput(userId) {
        this.emailNotVerified = false;
        this.forceErrorState = false;
        const isPhone = this.isPhoneNumber(userId);
        const isValidEmail = this.emailRegEx.test(userId);
        let valid = false;
        if (isPhone) {
            this.loginData.userId = userId;
            this.loginData.method = 'Phone';
            valid = true;
        }
        else if (isValidEmail) {
            this.loginData.userId = userId.trim();
            this.loginData.method = 'Email_Code';
            valid = true;
        }
        // Check to see if we're using the email or phone auth type
        if (this.oidLoginService.authType === 'phone' && valid) {
            valid = !isValidEmail;
        }
        else if (this.oidLoginService.authType === 'email' && valid) {
            valid = !isPhone;
        }
        this.isIDValid = valid;
    }
    /**
     * Begins the passwordless flow
     */
    startPasswordless() {
        if (this.f.invalid || this.emailNotVerified || !this.isIDValid) {
            this.forceErrorState = true;
            this.focusInvalidInput();
            return;
        }
        this.forceErrorState = false;
        this.oidLoginService.sendStateToHost({
            event: 'Passwordless Login',
            code: 'Started',
            success: false,
            data: null
        });
        this.showAlert = false;
        this.alert = null;
        if (this.isPhoneNumber(this.loginData.userId)) {
            this.loginData.userId = this.fixPhoneNumber(this.loginData.userId);
            this.loginData.method = 'Phone';
        }
        this.oidLoginService.setLoginInfo({
            widgetMode: 'login',
            userId: this.loginData.userId,
            method: this.loginData.method
        });
        this.loadSpinner.emit({
            loading: true,
            msg: this.dictionaryPipe.transform('ea-authenticate/loadingMsgs/forgotSendMFA')
        });
        this.timeoutService.stopWidgetTimeout();
        this.tokenService.startPasswordless(this.loginData.userId, this.loginData.method).then((res) => {
            this.loadSpinner.emit({
                loading: false
            });
            if (res.isSuccess) {
                this.oidLoginService.loginStep = 'oid-verify';
            }
            else {
                if (res.data.hasOwnProperty('statusText')) {
                    // Error before we reached the API. Probably not connected to VPN.
                    res.data = res.data;
                    this.alert = {
                        type: 'error',
                        heading: 'Something went wrong',
                        message: 'Could not reach the server. Please make sure you are connected to VPN. <br><b>Error message:</b> ' + res.data.statusText
                    };
                    this.showAlert = true;
                }
                else {
                    switch (res.message) {
                        case 'StartPasswordlessPhoneBeforeAuthenticatedEmail':
                            this.emailNotVerified = true;
                            this.loginData.userId = '';
                            this.f.control.controls.userId.markAsPristine();
                            this.f.control.controls.userId.markAsUntouched();
                            break;
                        default:
                            // Unknown/unhandled error.
                            this.loginData.userId = '';
                            this.f.control.controls.userId.markAsPristine();
                            this.f.control.controls.userId.markAsUntouched();
                            this.setAlert({
                                type: 'error',
                                heading: 'Something went wrong',
                                message: this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/unknown')
                            });
                    }
                }
            }
        });
    }
    changeRememberStatus(newVal) {
        if (newVal) {
            this.oidLoginService.setLoginInfo({ rememberUsername: true });
            this.loginData.rememberUsername = true;
        }
        else {
            this.oidLoginService.setLoginInfo({ rememberUsername: false });
            this.loginData.rememberUsername = false;
            localStorage.removeItem('ea-login-id');
        }
    }
    focusInvalidInput() {
        this.focusService.setFocus('userId');
    }
    setAlert(data) {
        this.alert = data;
        this.showAlert = true;
        this.preventAlertDismissal = true;
        // Prevent alert from being dismissed for 10 seconds.
        setTimeout(() => {
            this.preventAlertDismissal = false;
        }, 10000);
        this.oidLoginService.sendStateToHost({
            event: 'Alert',
            code: '',
            success: false,
            data
        });
    }
    clearAlert() {
        if (this.preventAlertDismissal) {
            return;
        }
        this.showAlert = false;
        this.alert = {
            type: null,
            heading: null,
            message: null
        };
        this.oidLoginService.loginStep = 'oid-start';
    }
    /**
     * If the password entry was deferred, this gives them a chance to reenter username/email
     */
    startOver() {
        this.clearAlert();
        this.shouldHideContinue = false;
        this.lockUsernameField = false;
    }
    /**
     * Click event from the external providers buttons.
     * @param btnInfo The data for the ext. provider
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loginBtnClick(btnInfo) {
        this.loadSpinner.emit({
            loading: true,
            msg: this.dictionaryPipe.transform('ea-authenticate/loadingMsgs/OIDCCreate')
        });
        this.oidcService.handleAuth0ButtonClick(btnInfo, this.discoveryDoc);
    }
    toggleAuthType() {
        if (this.oidLoginService.authType === 'email') {
            this.oidLoginService.authType = 'phone';
            this.setLabels();
        }
        else {
            this.oidLoginService.authType = 'email';
            this.setLabels();
        }
        this.loginData.userId = '';
        this.f?.control?.controls?.userId?.markAsPristine();
        this.f?.control?.controls?.userId?.markAsUntouched();
        this.isIDValid = false;
    }
    setLabels() {
        /** hardcode dictionary string for DX 4.4.2 release until issue with dictionary not loading is sorted out */
        switch (this.oidLoginService?.authType) {
            case 'email':
                this.userIdLabel = 'Email address';
                this.toggleAuthText = 'Log in with mobile number instead';
                this.idErrorText = 'Please enter a valid email address.';
                this.userIdInputMode = 'email';
                break;
            case 'phone':
                this.userIdLabel = 'Mobile number';
                this.toggleAuthText = 'Log in with email address instead';
                this.idErrorText = 'Please enter a valid mobile number.';
                this.userIdInputMode = 'tel';
                break;
            default:
                this.userIdLabel = 'Email address or mobile number';
                this.idErrorText = 'Please enter a valid email address or mobile number.';
                this.userIdInputMode = 'text';
        }
        // switch (this.oidLoginService?.authType) {
        //   case 'email':
        //     this.userIdLabel = this.dictionaryPipe.transform('ea-authenticate-oid-login/userId/labelEmail') as string;
        //     this.toggleAuthText = this.dictionaryPipe.transform('ea-authenticate-oid-login/toggleText/phone') as string;
        //     this.idErrorText = this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/emailInvalid') as string;
        //     this.userIdInputMode = 'email';
        //     break;
        //   case 'phone':
        //     this.userIdLabel = this.dictionaryPipe.transform('ea-authenticate-oid-login/userId/labelPhone') as string;
        //     this.toggleAuthText = this.dictionaryPipe.transform('ea-authenticate-oid-login/toggleText/email') as string;
        //     this.idErrorText = this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/phoneInvalid') as string;
        //     this.userIdInputMode = 'tel';
        //     break;
        //   default:
        //     this.userIdLabel = this.dictionaryPipe.transform('ea-authenticate-oid-login/userId/label') as string;
        //     this.idErrorText = this.dictionaryPipe.transform('ea-authenticate-oid-login/errors/idInvalid') as string;
        //     this.userIdInputMode = 'text';
        // }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidLoginComponent, deps: [{ token: i1.TokenService }, { token: EaWidgetContextService }, { token: OidLoginDataService }, { token: EaTimeoutService }, { token: i1.DictionaryPipe }, { token: i0.ChangeDetectorRef }, { token: i1.FocusService }, { token: OIDCService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: OidLoginComponent, selector: "oid-login", inputs: { shouldShowAlert: "shouldShowAlert" }, outputs: { loadSpinner: "loadSpinner" }, host: { listeners: { "window: keydown": "enter($event)" } }, viewQueries: [{ propertyName: "f", first: true, predicate: ["f"], descendants: true }], ngImport: i0, template: "<ng-container>\n  <form name=\"form\" #f=\"ngForm\">\n    <div class=\"form-group\">\n      <h1 class=\"bl-text-style--display-3 bl-font-color--black\">\n        {{ 'ea-authenticate-oid-login/title' | dictionary }}\n      </h1>\n      <div *ngIf=\"showSubtitle\">\n        <p class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n          {{ 'ea-authenticate-oid-login/subtitle' | dictionary }}\n        </p>\n      </div>\n    </div>\n\n    <div class=\"alert-holder\" *ngIf=\"showAlert\">\n      <ea-alert\n        alertId=\"{{ alert.type }}\"\n        alertStatus=\"{{ alert.type }}\"\n        [heading]=\"alert.heading\"\n        [message]=\"alert.message\"\n        class=\"bl-margin-bottom--16 bl-display--block bl-font-size--14\"\n      >\n      </ea-alert>\n    </div>\n\n    <!-- External login buttons -->\n    <div *ngIf=\"oidLoginService.redirectFlow\">\n      <ea-login-btn\n        *ngFor=\"let provider of oidLoginService.redirectButtonInfo; let i = index\"\n        [btnId]=\"'redirect-btn-' + i\"\n        [providerData]=\"provider\"\n        (clickAction)=\"loginBtnClick($event)\"\n        [attr.data-automation-id]=\"'redirect-login-button'\"\n      ></ea-login-btn>\n    </div>\n    <ea-separator *ngIf=\"oidLoginService.redirectFlow && oidLoginService.passwordlessFlow\" class=\"bl-margin-top--32 bl-margin-bottom--16\" textContent=\"or\"></ea-separator>\n\n    <div *ngIf=\"oidLoginService.passwordlessFlow\">\n      <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--24\">\n        <div *ngIf=\"!lockUsernameField\">\n          <label for=\"userId\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n            {{ userIdLabel }}\n          </label>\n          <input\n            required\n            #userId=\"ngModel\"\n            type=\"text\"\n            [attr.inputmode]=\"userIdInputMode\"\n            id=\"userId\"\n            name=\"userId\"\n            [maxlength]=\"widgetContextData?.config.emailMaxLength || '320'\"\n            class=\"bl-input bl-max-width--400\"\n            value=\"\"\n            [ngModel]=\"loginData.userId\"\n            [ngClass]=\"{ error: userId.touched && !isIDValid }\"\n            required=\"true\"\n            [attr.aria-invalid]=\"userId.invalid\"\n            aria-describedby=\"userid_error\"\n            (keydown)=\"checkForWhitespace($event)\"\n            (ngModelChange)=\"handleIDInput($event)\"\n          />\n\n          <span id=\"userid_error\" class=\"bl-error-msg bl-width--100\">\n            <ng-container *ngIf=\"(userId.touched && !isIDValid) || forceErrorState\">\n              {{ idErrorText }}\n            </ng-container>\n            <ng-container *ngIf=\"emailNotVerified\">\n              {{ 'ea-authenticate-oid-login/errors/emailNotVerified' | dictionary }}\n            </ng-container>\n          </span>\n        </div>\n\n        <div *ngIf=\"lockUsernameField\">\n          <p class=\"bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n            {{ 'ea-authenticate-oid-login/email/label' | dictionary }}\n          </p>\n          <div class=\"bl-display--flex bl-justify-content--space-between\">\n            <p>{{ loginData.userId | ellipses : 25 }}</p>\n            <a id=\"start-over\" (click)=\"startOver()\" tabindex=\"0\" class=\"bl-font-color--primary bl-margin-bottom--auto\">\n              {{ 'ea-authenticate-oid-login/changeEmail' | dictionary }}\n            </a>\n          </div>\n        </div>\n      </div>\n\n      <div\n        class=\"form-group bl-col-md-12 bl-padding-left--0 bl-margin-top--16 bl-margin-bottom--16 rememberMeCheckbox bl-display--flex bl-justify-content--space-between\"\n      >\n        <div class=\"bl-check-wrap\">\n          <input\n            type=\"checkbox\"\n            id=\"remember-me\"\n            name=\"rememberme\"\n            [(ngModel)]=\"loginData.rememberUsername\"\n            (ngModelChange)=\"changeRememberStatus($event)\"\n          />\n          <label for=\"remember-me\" class=\"bl-label bl-font-style-normal bl-font-size--14 bl-font-color--steel\">\n            {{ 'ea-authenticate-oid-login/rememberMe' | dictionary }}\n          </label>\n        </div>\n      </div>\n\n      <div>\n        <div class=\"bl-margin-y--24\">\n          <button\n            id=\"continue-btn\"\n            type=\"button\"\n            class=\"bl-button bl-button-primary bl-font-weight--600 bl-display--block bl-padding-bottom--12 bl-col-12 bl-padding-top--12\"\n            (click)=\"startPasswordless()\"\n          >\n            {{ 'ea-authenticate-oid-login/buttonText' | dictionary }}\n          </button>\n        </div>\n      </div>\n\n      <div\n        *ngIf=\"oidLoginService.authType !== 'default'\"\n        class=\"form-group bl-col-md-12 bl-padding-left--0 bl-margin-bottom--16 bl-text-align--center\"\n      >\n        <button type=\"button\" id=\"toggle-auth-type\" (click)=\"toggleAuthType()\" class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\">\n          {{ toggleAuthText }}\n        </button>\n      </div>\n    </div>\n    <div *ngIf=\"widgetContextData?.config.disclaimerCopy\" class=\"bl-margin-top--24 bl-margin-bottom--48\">\n      <p [innerHTML]=\"widgetContextData.config.disclaimerCopy\" class=\"bl-font-size--14\"></p>\n    </div>\n  </form>\n</ng-container>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i1.AlertComponent, selector: "ea-alert", inputs: ["alertId", "alertStatus", "heading", "message", "showAlert", "darkMode", "useNgContent"], outputs: ["closeEvent"] }, { kind: "directive", type: i7.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i7.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i7.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i7.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i7.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i7.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i7.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i7.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i7.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "component", type: LoginBtnComponent, selector: "ea-login-btn", inputs: ["providerData", "btnId"], outputs: ["clickAction"] }, { kind: "component", type: SeparatorComponent, selector: "ea-separator", inputs: ["textContent"] }, { kind: "pipe", type: i1.DictionaryPipe, name: "dictionary" }, { kind: "pipe", type: EllipsesPipe, name: "ellipses" }], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidLoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'oid-login', template: "<ng-container>\n  <form name=\"form\" #f=\"ngForm\">\n    <div class=\"form-group\">\n      <h1 class=\"bl-text-style--display-3 bl-font-color--black\">\n        {{ 'ea-authenticate-oid-login/title' | dictionary }}\n      </h1>\n      <div *ngIf=\"showSubtitle\">\n        <p class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n          {{ 'ea-authenticate-oid-login/subtitle' | dictionary }}\n        </p>\n      </div>\n    </div>\n\n    <div class=\"alert-holder\" *ngIf=\"showAlert\">\n      <ea-alert\n        alertId=\"{{ alert.type }}\"\n        alertStatus=\"{{ alert.type }}\"\n        [heading]=\"alert.heading\"\n        [message]=\"alert.message\"\n        class=\"bl-margin-bottom--16 bl-display--block bl-font-size--14\"\n      >\n      </ea-alert>\n    </div>\n\n    <!-- External login buttons -->\n    <div *ngIf=\"oidLoginService.redirectFlow\">\n      <ea-login-btn\n        *ngFor=\"let provider of oidLoginService.redirectButtonInfo; let i = index\"\n        [btnId]=\"'redirect-btn-' + i\"\n        [providerData]=\"provider\"\n        (clickAction)=\"loginBtnClick($event)\"\n        [attr.data-automation-id]=\"'redirect-login-button'\"\n      ></ea-login-btn>\n    </div>\n    <ea-separator *ngIf=\"oidLoginService.redirectFlow && oidLoginService.passwordlessFlow\" class=\"bl-margin-top--32 bl-margin-bottom--16\" textContent=\"or\"></ea-separator>\n\n    <div *ngIf=\"oidLoginService.passwordlessFlow\">\n      <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--24\">\n        <div *ngIf=\"!lockUsernameField\">\n          <label for=\"userId\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n            {{ userIdLabel }}\n          </label>\n          <input\n            required\n            #userId=\"ngModel\"\n            type=\"text\"\n            [attr.inputmode]=\"userIdInputMode\"\n            id=\"userId\"\n            name=\"userId\"\n            [maxlength]=\"widgetContextData?.config.emailMaxLength || '320'\"\n            class=\"bl-input bl-max-width--400\"\n            value=\"\"\n            [ngModel]=\"loginData.userId\"\n            [ngClass]=\"{ error: userId.touched && !isIDValid }\"\n            required=\"true\"\n            [attr.aria-invalid]=\"userId.invalid\"\n            aria-describedby=\"userid_error\"\n            (keydown)=\"checkForWhitespace($event)\"\n            (ngModelChange)=\"handleIDInput($event)\"\n          />\n\n          <span id=\"userid_error\" class=\"bl-error-msg bl-width--100\">\n            <ng-container *ngIf=\"(userId.touched && !isIDValid) || forceErrorState\">\n              {{ idErrorText }}\n            </ng-container>\n            <ng-container *ngIf=\"emailNotVerified\">\n              {{ 'ea-authenticate-oid-login/errors/emailNotVerified' | dictionary }}\n            </ng-container>\n          </span>\n        </div>\n\n        <div *ngIf=\"lockUsernameField\">\n          <p class=\"bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n            {{ 'ea-authenticate-oid-login/email/label' | dictionary }}\n          </p>\n          <div class=\"bl-display--flex bl-justify-content--space-between\">\n            <p>{{ loginData.userId | ellipses : 25 }}</p>\n            <a id=\"start-over\" (click)=\"startOver()\" tabindex=\"0\" class=\"bl-font-color--primary bl-margin-bottom--auto\">\n              {{ 'ea-authenticate-oid-login/changeEmail' | dictionary }}\n            </a>\n          </div>\n        </div>\n      </div>\n\n      <div\n        class=\"form-group bl-col-md-12 bl-padding-left--0 bl-margin-top--16 bl-margin-bottom--16 rememberMeCheckbox bl-display--flex bl-justify-content--space-between\"\n      >\n        <div class=\"bl-check-wrap\">\n          <input\n            type=\"checkbox\"\n            id=\"remember-me\"\n            name=\"rememberme\"\n            [(ngModel)]=\"loginData.rememberUsername\"\n            (ngModelChange)=\"changeRememberStatus($event)\"\n          />\n          <label for=\"remember-me\" class=\"bl-label bl-font-style-normal bl-font-size--14 bl-font-color--steel\">\n            {{ 'ea-authenticate-oid-login/rememberMe' | dictionary }}\n          </label>\n        </div>\n      </div>\n\n      <div>\n        <div class=\"bl-margin-y--24\">\n          <button\n            id=\"continue-btn\"\n            type=\"button\"\n            class=\"bl-button bl-button-primary bl-font-weight--600 bl-display--block bl-padding-bottom--12 bl-col-12 bl-padding-top--12\"\n            (click)=\"startPasswordless()\"\n          >\n            {{ 'ea-authenticate-oid-login/buttonText' | dictionary }}\n          </button>\n        </div>\n      </div>\n\n      <div\n        *ngIf=\"oidLoginService.authType !== 'default'\"\n        class=\"form-group bl-col-md-12 bl-padding-left--0 bl-margin-bottom--16 bl-text-align--center\"\n      >\n        <button type=\"button\" id=\"toggle-auth-type\" (click)=\"toggleAuthType()\" class=\"bl-font-color--primary bl-margin-bottom--auto bl-button bl-button-tertiary\">\n          {{ toggleAuthText }}\n        </button>\n      </div>\n    </div>\n    <div *ngIf=\"widgetContextData?.config.disclaimerCopy\" class=\"bl-margin-top--24 bl-margin-bottom--48\">\n      <p [innerHTML]=\"widgetContextData.config.disclaimerCopy\" class=\"bl-font-size--14\"></p>\n    </div>\n  </form>\n</ng-container>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}\n"] }]
        }], ctorParameters: () => [{ type: i1.TokenService }, { type: EaWidgetContextService }, { type: OidLoginDataService }, { type: EaTimeoutService }, { type: i1.DictionaryPipe }, { type: i0.ChangeDetectorRef }, { type: i1.FocusService }, { type: OIDCService }], propDecorators: { shouldShowAlert: [{
                type: Input
            }], loadSpinner: [{
                type: Output
            }], f: [{
                type: ViewChild,
                args: ['f']
            }], enter: [{
                type: HostListener,
                args: ['window: keydown', ['$event']]
            }] } });

class NumericOnlyDirective {
    constructor(_element) {
        this._element = _element;
    }
    onKeyDown(e) {
        if (
        // Allow: Delete, Backspace, Tab, Escape, Enter
        [46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && e.ctrlKey === true) || // Allow: Ctrl+A
            (e.keyCode === 67 && e.ctrlKey === true) || // Allow: Ctrl+C
            (e.keyCode === 86 && e.ctrlKey === true) || // Allow: Ctrl+V
            (e.keyCode === 88 && e.ctrlKey === true) || // Allow: Ctrl+X
            (e.keyCode === 65 && e.metaKey === true) || // Cmd+A (Mac)
            (e.keyCode === 67 && e.metaKey === true) || // Cmd+C (Mac)
            (e.keyCode === 86 && e.metaKey === true) || // Cmd+V (Mac)
            (e.keyCode === 88 && e.metaKey === true) || // Cmd+X (Mac)
            (e.keyCode >= 35 && e.keyCode <= 39) // Home, End, Left, Right
        ) {
            return; // let it happen, don't do anything
        }
        // Ensure that it is a number and stop other keypresses
        if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NumericOnlyDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: NumericOnlyDirective, selector: "[numericOnly]", host: { listeners: { "keydown": "onKeyDown($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NumericOnlyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[numericOnly]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });
class AlphaNumericOnlyDirective {
    constructor(_element) {
        this._element = _element;
    }
    onKeyDown(e) {
        if (e.shiftKey || (e.keyCode === 86 && e.ctrlKey === true) || (e.keyCode === 86 && e.metaKey === true)) {
            e.preventDefault();
        }
        if (
        // Allow: Delete, Backspace, Tab, Escape, Enter
        [46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && e.ctrlKey === true) || // Allow: Ctrl+A
            (e.keyCode === 67 && e.ctrlKey === true) || // Allow: Ctrl+C
            (e.keyCode === 88 && e.ctrlKey === true) || // Allow: Ctrl+X
            (e.keyCode === 65 && e.metaKey === true) || // Cmd+A (Mac)
            (e.keyCode === 67 && e.metaKey === true) || // Cmd+C (Mac)
            (e.keyCode === 88 && e.metaKey === true) || // Cmd+X (Mac)
            (e.keyCode >= 35 && e.keyCode <= 39) // Home, End, Left, Right
        ) {
            return; // let it happen, don't do anything
        }
        // Ensure that it is only chars & letters
        if ((e.keyCode >= 65 && e.keyCode <= 90) || (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
            return;
        }
        else {
            event.preventDefault();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AlphaNumericOnlyDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: AlphaNumericOnlyDirective, selector: "[alphanumericOnly]", host: { listeners: { "keydown": "onKeyDown($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AlphaNumericOnlyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[alphanumericOnly]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class OidCreateUserComponent {
    constructor(oidLoginService, widgetContextService, timeoutService, dictionary, router, route) {
        this.oidLoginService = oidLoginService;
        this.widgetContextService = widgetContextService;
        this.timeoutService = timeoutService;
        this.dictionary = dictionary;
        this.router = router;
        this.route = route;
        this.deferredCreationState = null;
        this.shouldShowAlert = false;
        /**
         * Provides a way to support additional form validation for
         * content in the `<ng-content />`
         */
        this.validOverride = true;
        this.loadSpinner = new EventEmitter();
        /**
         * Future state when we support non-email user names
         */
        this.isUsernameEnabled = false;
        /**
         * The default new user object.
         */
        this.newAppUser = {
            username: '',
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            countryCode: '1',
            preference: null,
            ssn: '',
            dob: '',
            ssnAvailable: null
        };
        /**
         * Yes Or No Radio button list otions
         */
        this.optionalSSNMessageListOptions = [
            { label: 'Yes', value: true },
            { label: 'No', value: false }
        ];
        /**
         * Used in the regex phone number check. After specials removed, is length of phone number valid.
         */
        // public phoneLengthValid: boolean;
        /**
         * Used to enable/disable form submission based on whether user has modified form. Needs a better name.
         */
        this.userExists = false;
        /**
         * Used to help mask the phone number.
         */
        this.maskedPhone = '';
        this.eConsentAccepted = false;
        // Utility functions
        this.checkForWhitespace = checkForWhitespace;
        this.emailRegEx = emailRegEx;
        // DOM boolean
        this.showAlert = false;
        this.showSubtitle = false;
        // Show SSN Field
        this.showSSN = false;
        // This variable will be ngModeled. The value sent in newUser.dob will be calculated from it.
        this.tempDob = '';
        // Allow to accept EConsent Checkbox
        this.allowToAccept = true;
        // Show Required to View EConsent Link Message
        this.showViewRequiredMsg = false;
    }
    /**
     * Create User component init
     */
    ngOnInit() {
        this.loadSpinner.emit({ loading: true });
        // Set the widget conext data. Lets evaluate whether this needs to use the subscription
        this.widgetContextData = this.widgetContextService.widgetContext;
        // Stop the timeout counter
        this.timeoutService.stopWidgetTimeout();
        if (this.oidLoginService.getLoginInfo('userId')) {
            this.newAppUser.email = this.oidLoginService.getLoginInfo('userId');
        }
        this.showSubtitle = this.dictionary.transform('ea-authenticate-oid-create/subtitle').length > 0;
        this.maxDob = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000);
        this.showSSN = this.canShowSSN();
        // If eConsent is not required, don't prevent user creation.
        if (!this.widgetContextData.eConsentFlow?.enabled) {
            this.eConsentAccepted = true;
        }
        // Set AllowToAccept variable if eConsent Accepted or ViewRequired econsent property is turned off.
        this.allowToAccept = this.eConsentAccepted === true ? true : !this.widgetContextData.eConsentFlow?.viewRequired;
        this.loadSpinner.emit({ loading: false });
    }
    /**
     * Monitors the deffered creation state input.
     * @param changes The simple changes object
     */
    ngOnChanges(changes) {
        if (changes.hasOwnProperty('deferredCreationState')) {
            this.deferredCreationState = changes.deferredCreationState.currentValue;
            switch (this.deferredCreationState) {
                case 'complete':
                    // Host app previously deferred creation and now wants to complete it.
                    this.proceedToLogin();
                    break;
                case 'fail':
                    // Host app previously deferred creation and now it has failed.
                    this.loadSpinner.emit({ loading: false });
                    this.alert = {
                        type: 'warning',
                        heading: this.dictionary.transform('ea-authenticate-oid-create/errors/failedDeferredCreate/title'),
                        message: this.dictionary.transform('ea-authenticate-oid-create/errors/failedDeferredCreate/detail')
                    };
                    this.showAlert = true;
                    break;
                default:
            }
            this.deferredCreationState = 'waiting';
        }
        if (changes.hasOwnProperty('shouldShowAlert')) {
            this.alert = this.oidLoginService.getAlert();
            this.showAlert = this.shouldShowAlert;
        }
    }
    /**
     * Called when the user pastes into the SSN field. Ensures only numbers are allowed.
     * Using new String triggers Angular's change detection so the value is updated properly.
     * @param val The pasted value
     */
    handleSSNPaste(val) {
        // eslint-disable-next-line no-new-wrappers
        this.newAppUser.ssn = new String(val.replace(/[^0-9\.]/g, ''));
    }
    /**
     * Helper to determine if the birthdate enters is within the given range since HTML max and min are not
     * enforced on manual entry in the datepicker. Also used for form validation.
     * @returns boolean
     */
    dobIsValid() {
        // If DOB is not enabled, don't prevent form submission.
        if (!this.widgetContextData?.createNewFlow?.optionalFields?.enableDOB) {
            return true;
        }
        if (this.tempDob.length < 8)
            return false;
        const day = this.tempDob.slice(0, 2);
        const month = this.tempDob.slice(2, 4);
        const year = this.tempDob.slice(4, 8);
        const date = new Date(`${year}/${day}/${month}`);
        const firstDate = new Date(1900, 1, 1);
        return date >= firstDate && date <= this.maxDob;
    }
    /**
     * Called on keyup to reenable form submission
     * @param event - the keyup event
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkButtonStatus(event) {
        if (event.keyCode !== 13 && event.keyCode !== 37 && event.keyCode !== 39) {
            this.userExists = false;
        }
    }
    /**
     * Switches to log in page from create page
     */
    logIn() {
        if (this.widgetContextService.widgetContext.loginFlow?.customLink) {
            // This should open in a new window unless user has popups blocked, then will redirect in same tab.
            const url = this.widgetContextService.widgetContext.loginFlow?.customLink;
            const isExternal = this.oidLoginService.isExternalURL(url);
            if (isExternal) {
                window.open(url, '_blank') || window.location.replace(url);
            }
            else {
                this.router.navigate([url], { relativeTo: this.route });
            }
        }
        else {
            this.oidLoginService.setLoginInfo({ widgetMode: 'login' });
        }
    }
    /**
     * Used by the phone number input to prevent any non-numeric input
     * @param event the js event
     * @returns boolean
     */
    restrictPhoneChar(event) {
        if (event.which >= 48 && event.which <= 57) {
            return true;
        }
        else {
            event.preventDefault();
            return false;
        }
    }
    /**
     * Create a new user account
     */
    createUser() {
        this.loadSpinner.emit({
            loading: true
        });
        this.oidLoginService.setLoginInfo({ widgetMode: 'create' });
        // Trim whitespace from beginning and end of First and Last name fields
        this.newAppUser.firstName = this.newAppUser.firstName.trim();
        this.newAppUser.lastName = this.newAppUser.lastName.trim();
        // Fix the DOB back to the old, expected format
        const day = this.tempDob.slice(0, 2);
        const month = this.tempDob.slice(2, 4);
        const year = this.tempDob.slice(4, 8);
        this.newAppUser.dob = `${year}-${day}-${month}`;
        // Store the user info for later.
        this.oidLoginService.newUser = this.newAppUser;
        // If they've set deferCreation, just emit the values to the host and wait for the host to trigger proceedToLogin.
        if (this.widgetContextData.createNewFlow?.deferredCreationState === 'defer') {
            this.oidLoginService.sendStateToHost({
                event: 'newUser',
                code: 'creationDeffered',
                success: true,
                data: {
                    firstName: this.newAppUser.firstName,
                    lastName: this.newAppUser.lastName,
                    dob: this.newAppUser.dob,
                    ssn: this.newAppUser.ssn,
                    email: this.newAppUser.email
                }
            });
        }
        else {
            this.proceedToLogin();
        }
    }
    /**
     * Once the user is created automatically start passwordless
     */
    proceedToLogin() {
        // Auto log the user in so they get the MFA screen immediately.
        this.oidLoginService.autoLoginInfo = {
            email: this.newAppUser.email
        };
        this.oidLoginService.setLoginInfo({ autoLogin: true });
        this.loadSpinner.emit({
            loading: false
        });
        this.oidLoginService.setLoginInfo({ widgetMode: 'login' });
        this.oidLoginService.loginStep = 'oid-start';
    }
    /**
     * Trims the whitespace from the end of email entries.
     * @param email The email address entered.
     */
    handleEmailInput(email) {
        this.newAppUser.email = email.trim();
    }
    showEConsent() {
        this.oidLoginService.sendStateToHost({
            event: 'eConsent',
            code: 'show',
            success: true,
            data: null
        });
        this.allowToAccept = true;
        this.setShowViewRequiredMsg();
    }
    /**
     * Setting SSN Available field value
     * @param ssnAvailable boolean
     */
    setSSNAvailability(ssnAvailable) {
        this.newAppUser.ssnAvailable = ssnAvailable;
        this.newAppUser.ssn = ssnAvailable ? this.newAppUser.ssn : '';
        this.showSSN = this.canShowSSN();
    }
    /**
     * Can show SSN field or not
     * @returns boolean
     */
    canShowSSN() {
        // SSN field must enabled
        const ssnEnabled = this.widgetContextData?.createNewFlow?.optionalFields?.enableSSN;
        if (ssnEnabled === true) {
            if (
            // If SSN made optional for heavy registration then display SSN field only if user selected SSN Available option
            (this.widgetContextData?.createNewFlow?.displayOptionalSSNMessage === true && this.newAppUser.ssnAvailable === true) ||
                this.widgetContextData?.createNewFlow?.displayOptionalSSNMessage === false)
                return true;
        }
        return false;
    }
    /**
     * Check allow to invoke click event.?
     * If View Required Message is displaying then disable click event
     */
    checkConsent() {
        this.setShowViewRequiredMsg();
        return this.showViewRequiredMsg === true ? false : true;
    }
    /**
     * If Consent Link set as 'Required to read' option then we need to display message and not to enable click event
     */
    setShowViewRequiredMsg() {
        if (this.allowToAccept !== true) {
            if (this.eConsentAccepted !== true) {
                this.showViewRequiredMsg = true;
                return;
            }
        }
        this.showViewRequiredMsg = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidCreateUserComponent, deps: [{ token: OidLoginDataService }, { token: EaWidgetContextService }, { token: EaTimeoutService }, { token: i1.DictionaryPipe }, { token: i5.Router }, { token: i5.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: OidCreateUserComponent, selector: "oid-create-user", inputs: { deferredCreationState: "deferredCreationState", shouldShowAlert: "shouldShowAlert", validOverride: "validOverride" }, outputs: { loadSpinner: "loadSpinner" }, providers: [DictionaryPipe], usesOnChanges: true, ngImport: i0, template: "<form name=\"form\" #f=\"ngForm\">\n  <div class=\"form-group\">\n    <h1 class=\"bl-font-weight--900 bl-font-color--black bl-font-size--28 bl-margin-bottom--8\">\n      {{ 'ea-authenticate-oid-create/title' | dictionary }}\n    </h1>\n\n    <p *ngIf=\"showSubtitle\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n      {{ 'ea-authenticate-oid-create/subtitle' | dictionary }}\n    </p>\n\n    <p class=\"bl-font-weight--800 bl-font-color--black\">\n      {{ 'ea-authenticate-oid-create/signUpLink/label' | dictionary }}\n      <a class=\"bl-text-decoration--none bl-font-weight--400 bl-font-color--primary\" tabindex=\"0\" (click)=\"logIn()\" id=\"login-button-1\">\n        {{ 'ea-authenticate-oid-create/signUpLink/linkText' | dictionary }}\n      </a>\n    </p>\n  </div>\n\n  <div class=\"alert-holder\" *ngIf=\"showAlert\">\n    <ea-alert\n      alertId=\"{{ alert.type }}\"\n      alertStatus=\"{{ alert.type }}\"\n      [heading]=\"alert.heading\"\n      [message]=\"alert.message\"\n      class=\"bl-margin-bottom--16 bl-display--block bl-font-size--14\"\n    >\n    </ea-alert>\n  </div>\n\n  <div *ngIf=\"widgetContextData\">\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--12\">\n      <label for=\"firstName\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/firstname/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        id=\"firstName\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.firstName\"\n        name=\"firstName\"\n        #firstName=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"firstName.invalid\"\n        aria-describedby=\"firstname_error\"\n        required\n        [ngClass]=\"{ error: firstName.invalid && firstName.touched }\"\n      />\n\n      <span id=\"firstname_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"firstName.invalid && firstName.touched\">\n        {{ 'ea-authenticate-oid-create/firstname/error' | dictionary }}\n      </span>\n    </div>\n\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"lastName\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/lastname/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        id=\"lastName\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.lastName\"\n        name=\"lastName\"\n        #lastName=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"lastName.invalid\"\n        aria-describedby=\"lastname_error\"\n        required\n        [ngClass]=\"{\n          error: lastName.invalid && lastName.touched\n        }\"\n      />\n\n      <span id=\"lastname_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"lastName.invalid && lastName.touched\">\n        {{ 'ea-authenticate-oid-create/lastname/error' | dictionary }}\n      </span>\n    </div>\n\n    <div *ngIf=\"isUsernameEnabled\" class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"username\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/username/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        alphanumericOnly\n        id=\"username\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.username\"\n        name=\"username\"\n        pattern=\"^[a-zA-Z0-9_.-]*$\"\n        #username=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"username.invalid\"\n        aria-describedby=\"username_error\"\n        required\n        [ngClass]=\"{\n          error: (username?.invalid && username?.touched) || (userExists && isUsernameEnabled)\n        }\"\n      />\n      <span id=\"username_error\" class=\"bl-error-msg bl-width--100\">\n        <ng-container *ngIf=\"username?.invalid && username?.touched\">\n          {{ 'ea-authenticate-oid-create/username/error' | dictionary }}\n        </ng-container>\n        <ng-container *ngIf=\"username?.touched && userExists && isUsernameEnabled\">\n          {{ 'ea-authenticate-oid-create/username/accountExists' | dictionary }}\n        </ng-container>\n      </span>\n    </div>\n\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"email\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/email/label' | dictionary }}\n      </label>\n\n      <input\n        (keyup)=\"checkButtonStatus($event)\"\n        id=\"email\"\n        required\n        #email=\"ngModel\"\n        class=\"bl-input\"\n        [pattern]=\"emailRegEx\"\n        type=\"text\"\n        name=\"email\"\n        [maxlength]=\"widgetContextData?.config.emailMaxLength || '320'\"\n        value=\"\"\n        [(ngModel)]=\"newAppUser.email\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"email.invalid\"\n        aria-describedby=\"email_error\"\n        [ngClass]=\"{\n          error: (email.invalid && email.touched) || (userExists && !isUsernameEnabled)\n        }\"\n        (ngModelChange)=\"handleEmailInput($event)\"\n        (keydown)=\"checkForWhitespace($event)\"\n      />\n\n      <span id=\"email_error\" class=\"bl-error-msg bl-width--100\">\n        <ng-container *ngIf=\"email.invalid && email.touched\">\n          {{ 'ea-authenticate-oid-create/email/error' | dictionary }}\n        </ng-container>\n        <ng-container *ngIf=\"email.touched && userExists && !isUsernameEnabled\">\n          {{ 'ea-authenticate-oid-create/email/accountExists' | dictionary }}\n        </ng-container>\n      </span>\n    </div>\n\n    <!-- Optional SSN confirmation field -->\n    <div\n      *ngIf=\"this.widgetContextData?.createNewFlow?.displayOptionalSSNMessage\"\n      class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\"\n    >\n      <span class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/ssnAvailable/label' | dictionary }}\n      </span>\n      <div class=\"bl-check-wrap bl-margin-bottom--5\">\n        <div class=\"bl-check-wrap bl-margin-bottom--5 bl-padding-y--4\" *ngFor=\"let item of optionalSSNMessageListOptions\">\n          <input\n            required\n            #ssnAvailable=\"ngModel\"\n            [(ngModel)]=\"newAppUser.ssnAvailable\"\n            type=\"radio\"\n            (change)=\"setSSNAvailability(item.value)\"\n            class=\"switch-input\"\n            name=\"ssnAvailable\"\n            id=\"{{ 'ssnAvailable-' + item.label }}\"\n            aria-required=\"true\"\n            [value]=\"item.value\"\n            [checked]=\"item.value === newAppUser.ssnAvailable\"\n            aria-describedby=\"ssnAvailable_error\"\n          />\n          <label for=\"{{ 'ssnAvailable-' + item.label }}\">\n            {{ item.label }}\n          </label>\n        </div>\n      </div>\n\n      <ng-container *ngIf=\"newAppUser?.ssnAvailable === false\">\n        <span\n          class=\"bl-text-style--tertiary-text-1 bl-display--block bl-padding-top--16\"\n          innerHtml=\"{{ 'ea-authenticate-oid-create/ssnAvailable/info' | dictionary }}\"\n        >\n        </span>\n      </ng-container>\n    </div>\n    <!-- End -->\n\n    <div *ngIf=\"showSSN\" class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"ssn\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/ssn/label' | dictionary }}\n      </label>\n      <div class=\"ssn-container bl-position--relative\">\n        <span class=\"ssn-prefix bl-font-color--silver bl-position--absolute\">XXX-XX-</span>\n        <input\n          type=\"text\"\n          inputmode=\"numeric\"\n          numericOnly\n          id=\"ssn\"\n          maxlength=\"4\"\n          minlength=\"4\"\n          class=\"bl-input\"\n          [ngModel]=\"newAppUser.ssn\"\n          (ngModelChange)=\"handleSSNPaste($event)\"\n          [ngClass]=\"{ error: ssn?.invalid && ssn?.touched }\"\n          name=\"ssn\"\n          pattern=\"^[0-9]*$\"\n          #ssn=\"ngModel\"\n          aria-required=\"true\"\n          [attr.aria-invalid]=\"ssn.invalid\"\n          aria-describedby=\"ssn_error\"\n          required\n        />\n      </div>\n      <span id=\"ssn_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"ssn?.invalid && ssn?.touched\">\n        {{ 'ea-authenticate-oid-create/ssn/error' | dictionary }}\n      </span>\n    </div>\n\n    <div\n      *ngIf=\"widgetContextData?.createNewFlow?.optionalFields?.enableDOB\"\n      class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\"\n    >\n      <label for=\"dob\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/dob/label' | dictionary }}\n      </label>\n\n      <input\n        mask=\"M0/d0/0000\"\n        inputmode=\"numeric\"\n        [leadZeroDateTime]=\"true\"\n        [validation]=\"true\"\n        placeholder=\"MM/DD/YYYY\"\n        (keyup)=\"checkButtonStatus($event)\"\n        id=\"dob\"\n        required\n        #dob=\"ngModel\"\n        class=\"bl-input\"\n        type=\"text\"\n        name=\"dob\"\n        [(ngModel)]=\"tempDob\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"dob?.invalid\"\n        aria-describedby=\"dob_error\"\n        [ngClass]=\"{ error: (dob?.invalid || !dobIsValid()) && dob?.touched }\"\n      />\n\n      <span *ngIf=\"(dob?.invalid || !dobIsValid()) && dob?.touched\" id=\"dob_error\" class=\"bl-error-msg bl-width--100\">\n        {{ 'ea-authenticate-oid-create/dob/error' | dictionary }}\n      </span>\n    </div>\n    <div class=\"bl-margin-top--24 bl-margin-bottom--8\">\n      <ng-content></ng-content>\n    </div>\n\n    <ng-container *ngIf=\"widgetContextData?.eConsentFlow?.enabled\">\n      <div class=\"bl-check-wrap bl-margin-top--24 bl-margin-bottom--8\">\n        <input\n          class=\"bl-display--inline-block\"\n          type=\"checkbox\"\n          name=\"heavy-reg-econsent\"\n          id=\"heavy-reg-econsent\"\n          [attr.data-automation-id]=\"'econsent'\"\n          required\n          aria-describedby=\"econsent-desc\"\n          [(ngModel)]=\"eConsentAccepted\"\n          (click)=\"checkConsent()\"\n        />\n        <label class=\"bl-label bl-font-color--black bl-font-weight--600 bl-font-size--14\" for=\"heavy-reg-econsent\">\n          {{ 'ea-authenticate-oid-create/econsentLinkTextPrefix' | dictionary }}\n          <a id=\"heavy-reg-econsent-link\" [attr.data-automation-id]=\"'econsent-link'\" href=\"javascript:void(0)\" (click)=\"showEConsent()\">{{\n            'ea-authenticate-oid-create/econsentLinkText' | dictionary\n          }}</a>\n          {{ 'ea-authenticate-oid-create/econsentLinkTextSuffix' | dictionary }}\n        </label>\n      </div>\n      <span *ngIf=\"!eConsentAccepted\" id=\"econsent-desc\" class=\"bl-screen-reader-only\">\n        {{ 'ea-authenticate-oid-create/econsentDescription' | dictionary }}\n      </span>\n      <span *ngIf=\"showViewRequiredMsg\" id=\"econsent-view-required\" class=\"bl-error-msg\">\n        {{ 'ea-authenticate-oid-create/econsentViewRequired' | dictionary }}\n      </span>\n    </ng-container>\n    <div class=\"bl-margin-bottom--40 bl-margin-top--32\">\n      <button\n        id=\"create-user-btn\"\n        class=\"bl-button bl-button-primary bl-font-weight--600 bl-display--block bl-padding-bottom--12 bl-col-12 bl-padding-top--12\"\n        [class.bl-disabled]=\"f.invalid || userExists || !dobIsValid() || !eConsentAccepted || !validOverride\"\n        [disabled]=\"f.invalid || userExists || !dobIsValid() || !eConsentAccepted || !validOverride\"\n        (click)=\"createUser()\"\n      >\n        {{ 'ea-authenticate-oid-create/button' | dictionary }}\n      </button>\n    </div>\n  </div>\n</form>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}.ssn-container span{left:10px;top:15px}.ssn-container input{padding-left:75px}input::-webkit-calendar-picker-indicator{display:none}\n"], dependencies: [{ kind: "directive", type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i1.AlertComponent, selector: "ea-alert", inputs: ["alertId", "alertStatus", "heading", "message", "showAlert", "darkMode", "useNgContent"], outputs: ["closeEvent"] }, { kind: "directive", type: i7.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i7.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i7.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i7.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i7.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i7.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i7.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i7.MinLengthValidator, selector: "[minlength][formControlName],[minlength][formControl],[minlength][ngModel]", inputs: ["minlength"] }, { kind: "directive", type: i7.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i7.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i7.CheckboxRequiredValidator, selector: "input[type=checkbox][required][formControlName],input[type=checkbox][required][formControl],input[type=checkbox][required][ngModel]" }, { kind: "directive", type: i7.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i7.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i8.NgxMaskDirective, selector: "input[mask], textarea[mask]", inputs: ["mask", "specialCharacters", "patterns", "prefix", "suffix", "thousandSeparator", "decimalMarker", "dropSpecialCharacters", "hiddenInput", "showMaskTyped", "placeHolderCharacter", "shownMaskExpression", "showTemplate", "clearIfNotMatch", "validation", "separatorLimit", "allowNegativeNumbers", "leadZeroDateTime", "leadZero", "triggerOnMaskChange", "apm", "inputTransformFn", "outputTransformFn", "keepCharacterPositions"], outputs: ["maskFilled"], exportAs: ["mask", "ngxMask"] }, { kind: "directive", type: NumericOnlyDirective, selector: "[numericOnly]" }, { kind: "directive", type: AlphaNumericOnlyDirective, selector: "[alphanumericOnly]" }, { kind: "pipe", type: i1.DictionaryPipe, name: "dictionary" }], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OidCreateUserComponent, decorators: [{
            type: Component,
            args: [{ selector: 'oid-create-user', providers: [DictionaryPipe], template: "<form name=\"form\" #f=\"ngForm\">\n  <div class=\"form-group\">\n    <h1 class=\"bl-font-weight--900 bl-font-color--black bl-font-size--28 bl-margin-bottom--8\">\n      {{ 'ea-authenticate-oid-create/title' | dictionary }}\n    </h1>\n\n    <p *ngIf=\"showSubtitle\" class=\"bl-font-size--16 bl-font-style-normal bl-font-weight-normal bl-font-color--black bl-margin-bottom--8\">\n      {{ 'ea-authenticate-oid-create/subtitle' | dictionary }}\n    </p>\n\n    <p class=\"bl-font-weight--800 bl-font-color--black\">\n      {{ 'ea-authenticate-oid-create/signUpLink/label' | dictionary }}\n      <a class=\"bl-text-decoration--none bl-font-weight--400 bl-font-color--primary\" tabindex=\"0\" (click)=\"logIn()\" id=\"login-button-1\">\n        {{ 'ea-authenticate-oid-create/signUpLink/linkText' | dictionary }}\n      </a>\n    </p>\n  </div>\n\n  <div class=\"alert-holder\" *ngIf=\"showAlert\">\n    <ea-alert\n      alertId=\"{{ alert.type }}\"\n      alertStatus=\"{{ alert.type }}\"\n      [heading]=\"alert.heading\"\n      [message]=\"alert.message\"\n      class=\"bl-margin-bottom--16 bl-display--block bl-font-size--14\"\n    >\n    </ea-alert>\n  </div>\n\n  <div *ngIf=\"widgetContextData\">\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--12\">\n      <label for=\"firstName\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/firstname/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        id=\"firstName\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.firstName\"\n        name=\"firstName\"\n        #firstName=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"firstName.invalid\"\n        aria-describedby=\"firstname_error\"\n        required\n        [ngClass]=\"{ error: firstName.invalid && firstName.touched }\"\n      />\n\n      <span id=\"firstname_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"firstName.invalid && firstName.touched\">\n        {{ 'ea-authenticate-oid-create/firstname/error' | dictionary }}\n      </span>\n    </div>\n\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"lastName\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/lastname/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        id=\"lastName\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.lastName\"\n        name=\"lastName\"\n        #lastName=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"lastName.invalid\"\n        aria-describedby=\"lastname_error\"\n        required\n        [ngClass]=\"{\n          error: lastName.invalid && lastName.touched\n        }\"\n      />\n\n      <span id=\"lastname_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"lastName.invalid && lastName.touched\">\n        {{ 'ea-authenticate-oid-create/lastname/error' | dictionary }}\n      </span>\n    </div>\n\n    <div *ngIf=\"isUsernameEnabled\" class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"username\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/username/label' | dictionary }}\n      </label>\n\n      <input\n        type=\"text\"\n        alphanumericOnly\n        id=\"username\"\n        maxlength=\"60\"\n        class=\"bl-input\"\n        [(ngModel)]=\"newAppUser.username\"\n        name=\"username\"\n        pattern=\"^[a-zA-Z0-9_.-]*$\"\n        #username=\"ngModel\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"username.invalid\"\n        aria-describedby=\"username_error\"\n        required\n        [ngClass]=\"{\n          error: (username?.invalid && username?.touched) || (userExists && isUsernameEnabled)\n        }\"\n      />\n      <span id=\"username_error\" class=\"bl-error-msg bl-width--100\">\n        <ng-container *ngIf=\"username?.invalid && username?.touched\">\n          {{ 'ea-authenticate-oid-create/username/error' | dictionary }}\n        </ng-container>\n        <ng-container *ngIf=\"username?.touched && userExists && isUsernameEnabled\">\n          {{ 'ea-authenticate-oid-create/username/accountExists' | dictionary }}\n        </ng-container>\n      </span>\n    </div>\n\n    <div class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"email\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/email/label' | dictionary }}\n      </label>\n\n      <input\n        (keyup)=\"checkButtonStatus($event)\"\n        id=\"email\"\n        required\n        #email=\"ngModel\"\n        class=\"bl-input\"\n        [pattern]=\"emailRegEx\"\n        type=\"text\"\n        name=\"email\"\n        [maxlength]=\"widgetContextData?.config.emailMaxLength || '320'\"\n        value=\"\"\n        [(ngModel)]=\"newAppUser.email\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"email.invalid\"\n        aria-describedby=\"email_error\"\n        [ngClass]=\"{\n          error: (email.invalid && email.touched) || (userExists && !isUsernameEnabled)\n        }\"\n        (ngModelChange)=\"handleEmailInput($event)\"\n        (keydown)=\"checkForWhitespace($event)\"\n      />\n\n      <span id=\"email_error\" class=\"bl-error-msg bl-width--100\">\n        <ng-container *ngIf=\"email.invalid && email.touched\">\n          {{ 'ea-authenticate-oid-create/email/error' | dictionary }}\n        </ng-container>\n        <ng-container *ngIf=\"email.touched && userExists && !isUsernameEnabled\">\n          {{ 'ea-authenticate-oid-create/email/accountExists' | dictionary }}\n        </ng-container>\n      </span>\n    </div>\n\n    <!-- Optional SSN confirmation field -->\n    <div\n      *ngIf=\"this.widgetContextData?.createNewFlow?.displayOptionalSSNMessage\"\n      class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\"\n    >\n      <span class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/ssnAvailable/label' | dictionary }}\n      </span>\n      <div class=\"bl-check-wrap bl-margin-bottom--5\">\n        <div class=\"bl-check-wrap bl-margin-bottom--5 bl-padding-y--4\" *ngFor=\"let item of optionalSSNMessageListOptions\">\n          <input\n            required\n            #ssnAvailable=\"ngModel\"\n            [(ngModel)]=\"newAppUser.ssnAvailable\"\n            type=\"radio\"\n            (change)=\"setSSNAvailability(item.value)\"\n            class=\"switch-input\"\n            name=\"ssnAvailable\"\n            id=\"{{ 'ssnAvailable-' + item.label }}\"\n            aria-required=\"true\"\n            [value]=\"item.value\"\n            [checked]=\"item.value === newAppUser.ssnAvailable\"\n            aria-describedby=\"ssnAvailable_error\"\n          />\n          <label for=\"{{ 'ssnAvailable-' + item.label }}\">\n            {{ item.label }}\n          </label>\n        </div>\n      </div>\n\n      <ng-container *ngIf=\"newAppUser?.ssnAvailable === false\">\n        <span\n          class=\"bl-text-style--tertiary-text-1 bl-display--block bl-padding-top--16\"\n          innerHtml=\"{{ 'ea-authenticate-oid-create/ssnAvailable/info' | dictionary }}\"\n        >\n        </span>\n      </ng-container>\n    </div>\n    <!-- End -->\n\n    <div *ngIf=\"showSSN\" class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\">\n      <label for=\"ssn\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/ssn/label' | dictionary }}\n      </label>\n      <div class=\"ssn-container bl-position--relative\">\n        <span class=\"ssn-prefix bl-font-color--silver bl-position--absolute\">XXX-XX-</span>\n        <input\n          type=\"text\"\n          inputmode=\"numeric\"\n          numericOnly\n          id=\"ssn\"\n          maxlength=\"4\"\n          minlength=\"4\"\n          class=\"bl-input\"\n          [ngModel]=\"newAppUser.ssn\"\n          (ngModelChange)=\"handleSSNPaste($event)\"\n          [ngClass]=\"{ error: ssn?.invalid && ssn?.touched }\"\n          name=\"ssn\"\n          pattern=\"^[0-9]*$\"\n          #ssn=\"ngModel\"\n          aria-required=\"true\"\n          [attr.aria-invalid]=\"ssn.invalid\"\n          aria-describedby=\"ssn_error\"\n          required\n        />\n      </div>\n      <span id=\"ssn_error\" class=\"bl-error-msg bl-width--100\" *ngIf=\"ssn?.invalid && ssn?.touched\">\n        {{ 'ea-authenticate-oid-create/ssn/error' | dictionary }}\n      </span>\n    </div>\n\n    <div\n      *ngIf=\"widgetContextData?.createNewFlow?.optionalFields?.enableDOB\"\n      class=\"form-group bl-col-md-12 bl-padding-left--0 bl-padding-right--0 bl-margin-top-md--16\"\n    >\n      <label for=\"dob\" class=\"bl-label bl-font-style-normal bl-font-weight--800 bl-font-size--14 bl-font-color--steel\">\n        {{ 'ea-authenticate-oid-create/dob/label' | dictionary }}\n      </label>\n\n      <input\n        mask=\"M0/d0/0000\"\n        inputmode=\"numeric\"\n        [leadZeroDateTime]=\"true\"\n        [validation]=\"true\"\n        placeholder=\"MM/DD/YYYY\"\n        (keyup)=\"checkButtonStatus($event)\"\n        id=\"dob\"\n        required\n        #dob=\"ngModel\"\n        class=\"bl-input\"\n        type=\"text\"\n        name=\"dob\"\n        [(ngModel)]=\"tempDob\"\n        aria-required=\"true\"\n        [attr.aria-invalid]=\"dob?.invalid\"\n        aria-describedby=\"dob_error\"\n        [ngClass]=\"{ error: (dob?.invalid || !dobIsValid()) && dob?.touched }\"\n      />\n\n      <span *ngIf=\"(dob?.invalid || !dobIsValid()) && dob?.touched\" id=\"dob_error\" class=\"bl-error-msg bl-width--100\">\n        {{ 'ea-authenticate-oid-create/dob/error' | dictionary }}\n      </span>\n    </div>\n    <div class=\"bl-margin-top--24 bl-margin-bottom--8\">\n      <ng-content></ng-content>\n    </div>\n\n    <ng-container *ngIf=\"widgetContextData?.eConsentFlow?.enabled\">\n      <div class=\"bl-check-wrap bl-margin-top--24 bl-margin-bottom--8\">\n        <input\n          class=\"bl-display--inline-block\"\n          type=\"checkbox\"\n          name=\"heavy-reg-econsent\"\n          id=\"heavy-reg-econsent\"\n          [attr.data-automation-id]=\"'econsent'\"\n          required\n          aria-describedby=\"econsent-desc\"\n          [(ngModel)]=\"eConsentAccepted\"\n          (click)=\"checkConsent()\"\n        />\n        <label class=\"bl-label bl-font-color--black bl-font-weight--600 bl-font-size--14\" for=\"heavy-reg-econsent\">\n          {{ 'ea-authenticate-oid-create/econsentLinkTextPrefix' | dictionary }}\n          <a id=\"heavy-reg-econsent-link\" [attr.data-automation-id]=\"'econsent-link'\" href=\"javascript:void(0)\" (click)=\"showEConsent()\">{{\n            'ea-authenticate-oid-create/econsentLinkText' | dictionary\n          }}</a>\n          {{ 'ea-authenticate-oid-create/econsentLinkTextSuffix' | dictionary }}\n        </label>\n      </div>\n      <span *ngIf=\"!eConsentAccepted\" id=\"econsent-desc\" class=\"bl-screen-reader-only\">\n        {{ 'ea-authenticate-oid-create/econsentDescription' | dictionary }}\n      </span>\n      <span *ngIf=\"showViewRequiredMsg\" id=\"econsent-view-required\" class=\"bl-error-msg\">\n        {{ 'ea-authenticate-oid-create/econsentViewRequired' | dictionary }}\n      </span>\n    </ng-container>\n    <div class=\"bl-margin-bottom--40 bl-margin-top--32\">\n      <button\n        id=\"create-user-btn\"\n        class=\"bl-button bl-button-primary bl-font-weight--600 bl-display--block bl-padding-bottom--12 bl-col-12 bl-padding-top--12\"\n        [class.bl-disabled]=\"f.invalid || userExists || !dobIsValid() || !eConsentAccepted || !validOverride\"\n        [disabled]=\"f.invalid || userExists || !dobIsValid() || !eConsentAccepted || !validOverride\"\n        (click)=\"createUser()\"\n      >\n        {{ 'ea-authenticate-oid-create/button' | dictionary }}\n      </button>\n    </div>\n  </div>\n</form>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}.ssn-container span{left:10px;top:15px}.ssn-container input{padding-left:75px}input::-webkit-calendar-picker-indicator{display:none}\n"] }]
        }], ctorParameters: () => [{ type: OidLoginDataService }, { type: EaWidgetContextService }, { type: EaTimeoutService }, { type: i1.DictionaryPipe }, { type: i5.Router }, { type: i5.ActivatedRoute }], propDecorators: { deferredCreationState: [{
                type: Input
            }], shouldShowAlert: [{
                type: Input
            }], validOverride: [{
                type: Input
            }], loadSpinner: [{
                type: Output
            }] } });

class EaAuthenticateComponent {
    constructor(loginService, tokenService, widgetContextService, dictionary, cd, route, location, oidcService, dictionaryPipe) {
        this.loginService = loginService;
        this.tokenService = tokenService;
        this.widgetContextService = widgetContextService;
        this.dictionary = dictionary;
        this.cd = cd;
        this.route = route;
        this.location = location;
        this.oidcService = oidcService;
        this.dictionaryPipe = dictionaryPipe;
        /**
         * Provides a way to support additional form validation for
         * content in the `<ng-content />`
         */
        this.validOverride = true;
        // Export Data
        this.widgetExportDataChange = new EventEmitter();
        this.widgetInfoStateChange = new EventEmitter();
        // Boolean for load spinner overlay
        this.loading = false;
        // Referenced in the ngClass of the load spinner.
        this.loadingClass = '';
        // Optional message for load spinner
        this.loadSpinnerMsg = '';
        // Passes a deferred create state into the create user component
        this.defferedCreationState = null;
        // Holder for subscriptions
        this.subscriptions = new Subscription();
        // Auto-generated widget version info;
        this.widgetVersion = WidgetVersionInfo.widgetVersion;
        this.widgetName = WidgetVersionInfo.widgetName;
        this.widgetStartStates = ['oid-start', 'timeout'];
        this.shouldShowAlert = false;
        this.isPhoneNumber = isPhoneNumber;
        this.fixPhoneNumber = fixPhoneNumber;
        window.onpageshow = (event) => {
            if (event.persisted) {
                this.cleanUpForRedirect();
            }
        };
    }
    /**
     * Initialize the login widget
     */
    ngOnInit() {
        window.onbeforeunload = () => {
            this.ngOnDestroy();
        };
        this.loadSpinnerHandler({
            loading: true
        });
        // Initialize settings
        this.loginService.clearData();
        // Set the widget context data
        this.widgetContextData = this.widgetContextService.initWidgetContext(this.widgetContext);
        // Set the authentication type, if present
        if (this.widgetContextData.authenticationType) {
            this.loginService.authType = this.widgetContextData.authenticationType;
        }
        // If an alternate cache name is provided, configure OID to use it
        if (this.widgetContextData.config?.tokenCacheNameOverride) {
            this.tokenService.configureCache('localStorage', this.widgetContextData.config.tokenCacheNameOverride);
        }
        this.startPageStep = 'oid-start';
        this.loginService.loginStep = this.startPageStep;
        this.loadSpinnerHandler({ loading: true });
        const isDxMode = this.widgetContextData.oid.dxMode || false;
        this.tokenService
            .initialize(this.widgetContextData.oid.apiBaseURL, {}, this.widgetContextData.oid.oidConfigName, isDxMode)
            .then((res) => {
            if (res.isSuccess) {
                this.loginService.setOidConfig(res.data);
                if (this.loginService.redirectFlow) {
                    this.tokenService.getDiscoveryDoc(res.data.discoveryURL).then((doc) => {
                        this.loginService.setDiscoveryDoc(doc.data);
                        this.loginService.oidReady.next(true);
                        this.loadSpinnerHandler({ loading: false });
                    });
                }
                else {
                    this.loginService.oidReady.next(true);
                    this.loadSpinnerHandler({ loading: false });
                }
            }
            else {
                // TODO: Should this err be more explicit? This means the oid configuration could not be retrieved but
                // the api call was successful.
                this.handleUnknownError();
                console.warn(res.message);
            }
        });
        // TODO: Theme service needs to be fired if in agnostic mode.
        // This is how export data gets to the host upon completion of login sequence.
        const exportSub1 = this.loginService.widgetExportData.subscribe((newExportData) => {
            this.exportWidgetData(newExportData);
        });
        // This is how other data is exported to the host.
        const exportSub2 = this.loginService.widgetStateExportData.subscribe((stateExportData) => {
            this.widgetStateExportData(stateExportData);
        });
        const alertSub = this.loginService.alertSet.subscribe((alertInfo) => {
            // Check to see if widget has the alert boolean set. If so, honor it. If not, assume they're using the old way.
            if (this.widgetContextData.hasOwnProperty('preventAlertRedirect')) {
                this.shouldShowAlert = this.widgetContextData.preventAlertRedirect;
            }
            else {
                this.shouldShowAlert = false;
            }
        });
        this.subscriptions.add(exportSub1);
        this.subscriptions.add(exportSub2);
        this.subscriptions.add(alertSub);
        // Dictionary Service substitutes hardcoded strings with variables driven from json library. EA required.
        this.setUpEADictionaryService();
        // If a deep link was provided in the widget context, handle it.
        this.checkForDeepLink();
        // Process any query params present in the URL.
        this.handleUrlQueryParams();
        // If the widget context includes setAlert, set it.
        if (this.widgetContextData.hasOwnProperty('setAlert')) {
            this.loginService.setupAlert(this.widgetContextData.setAlert);
        }
        // If there is custom eConsent text, modify the dictionary.
        if (this.widgetContextData.eConsentFlow?.linkText) {
            this.dictionary.updateJSON({
                'ea-authenticate-oid-create': {
                    econsentLinkTextPrefix: this.widgetContextData.eConsentFlow.linkTextPrefix,
                    econsentLinkText: this.widgetContextData.eConsentFlow.linkText,
                    econsentLinkTextSuffix: this.widgetContextData.eConsentFlow.linkTextSuffix
                }
            });
        }
        if (!this.loginService.redirectInProgress && this.loginService.loginStep === null) {
            this.loadSpinnerHandler({
                loading: false
            });
            this.loginService.loginStep = this.startPageStep;
        }
    }
    /**
     * This only checks to see if an alert has been added to the context, and sets it if it has.
     * @param changes The changes object.
     */
    ngOnChanges(changes) {
        if (changes.hasOwnProperty('widgetContext')) {
            this.widgetContextData = this.widgetContextService.initWidgetContext(changes.widgetContext.currentValue);
            if (changes.widgetContext.currentValue.hasOwnProperty('setAlert')) {
                if (changes.widgetContext.currentValue.setAlert === null) {
                    this.loginService.setupAlert(null);
                }
                else {
                    this.loginService.setupAlert({
                        type: changes.widgetContext.currentValue.setAlert.type,
                        heading: changes.widgetContext.currentValue.setAlert.heading,
                        message: changes.widgetContext.currentValue.setAlert.message
                    });
                }
                if (!this.widgetContextData.hasOwnProperty('preventAlertRedirect') || !this.widgetContextData.preventAlertRedirect) {
                    this.loginService.loginStep = 'reset';
                    setTimeout(() => {
                        this.loginService.loginStep = this.startPageStep;
                    });
                }
            }
            if (this.widgetContextData.hasOwnProperty('createNewFlow')) {
                if (this.widgetContextData.createNewFlow.hasOwnProperty('deferredCreationState')) {
                    // eslint-disable-next-line no-new-wrappers
                    this.defferedCreationState = this.widgetContextData.createNewFlow.deferredCreationState.toString();
                    this.cd.detectChanges();
                    // Setting this back to defer to prevent a user from bypassing the deferment by trying multiple times.
                    this.widgetContextData.createNewFlow.deferredCreationState = 'defer';
                    this.defferedCreationState = 'defer';
                }
            }
        }
    }
    ngOnDestroy() {
        this.loginService.clearData();
        this.subscriptions.unsubscribe();
    }
    /**
     * Consumes deep links from the widget context.
     */
    checkForDeepLink() {
        if (this.widgetContextData.emailPrefill) {
            this.loginService.setLoginInfo({
                userId: this.widgetContextData.emailPrefill
            });
        }
        switch (this.widgetContextData.deepLink) {
            case 'login':
            case 'create':
                this.loginService.setLoginInfo({
                    widgetMode: this.widgetContextData.deepLink
                });
                break;
            case 'oid-verify':
                const userId = this.widgetContextData.oid.verifyUserId;
                const method = this.isPhoneNumber(userId) ? 'phone' : 'email_code'; // This assumes valid input.
                this.loginService.setLoginInfo({ userId, method, verifyOnly: true });
                this.loginService.loginStep = 'oid-verify';
                this.loadSpinnerHandler({ loading: false });
                break;
            default:
                this.loginService.setLoginInfo({ widgetMode: 'login' });
        }
    }
    /**
     * Checks the URL for query parameter. Handles them. Including deeplinks and the returns from redirects.
     */
    handleUrlQueryParams() {
        const sub = this.route.queryParamMap.subscribe((params) => {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const qParams = params['params'];
            if (!Object.keys(qParams).length) {
                // If there is an ea-oidc object but no params, the user did not complete the redirected
                // login, so let's start them over.
                sessionStorage.removeItem('ea-oidc');
                return;
            }
            // if (qParams.eaRedirect) {
            //   // Handle redirect options.
            //   // Future state
            // }
            // Code comes from an OIDC login
            if (qParams.code) {
                const oidcObj = JSON.parse(sessionStorage.getItem('ea-oidc'));
                if (!oidcObj) {
                    console.warn('Url had query params with a code but no oidc redirect obj found in sessionStorage.');
                    return;
                }
                const stateMatches = oidcObj.params.state === qParams.state;
                this.loginService.setLoginInfo({ authProviderId: oidcObj.providerId });
                if (stateMatches) {
                    this.loadSpinnerHandler({
                        loading: true,
                        msg: this.dictionaryPipe.transform('ea-authenticate/loadingMsgs/startOIDC')
                    });
                    // Set a flag to prevent dismissing load spinner.
                    this.loginService.redirectInProgress = true;
                    // Redirect user to external IdP
                    this.handleRedirect(qParams, oidcObj);
                }
                else {
                    this.loginService.redirectInProgress = false;
                    this.handleUnknownError();
                }
            }
        });
        this.subscriptions.add(sub);
    }
    handleRedirect(qParams, oidcObj) {
        this.oidcService
            .handleOIDCode(qParams.code, oidcObj)
            .then((res) => {
            // If no id_token is present, abort with an error.
            if (!res.hasOwnProperty('id_token')) {
                this.loadSpinnerHandler({
                    loading: false
                });
                this.loginService.redirectInProgress = false;
                this.handleUnknownError();
                return;
            }
            // First validate the nonce;
            const decodedToken = this.decodeTokenHelper(res.id_token);
            const newOidcObj = JSON.parse(sessionStorage.getItem('ea-oidc'));
            const nonceMatches = decodedToken.nonce === newOidcObj.params.nonce;
            // Store the email in the login service if its present in the session storage object.
            if (newOidcObj.rememberMeEmail) {
                this.loginService.setLoginInfo({
                    rememberUsername: true,
                    userId: newOidcObj.rememberMeEmail
                });
            }
            // Remove the ea-oidc from session storage
            sessionStorage.removeItem('ea-oidc');
            if (nonceMatches) {
                this.tokenService.updateToken(res);
                this.loginService.sendDataToHost(null, true, 'auth0');
            }
            else {
                this.loadSpinnerHandler({
                    loading: false
                });
                this.loginService.redirectInProgress = false;
                this.handleUnknownError();
            }
        })
            .catch((err) => {
            console.error(err);
            this.loginService.redirectInProgress = false;
            this.handleUnknownError();
        });
    }
    decodeTokenHelper(tokenToDecode) {
        // eslint-disable-next-line max-len
        // This fix for utf8 came from https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
        const base64Url = tokenToDecode.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window
            .atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        return JSON.parse(jsonPayload);
    }
    /**
     * Export data from widget. If agnostic application, we're dealing in strings and capture a custom event.
     * If Angular, we can use built in two-way property bindings.
     * @param exportData - the data object to emit to the host app
     */
    exportWidgetData(exportData) {
        // Set the export data based on what mode we are in
        this.widgetExportData = this.loginService.appIsAgnostic ? JSON.stringify(exportData) : exportData;
        // Emit the data to the host app
        this.widgetExportDataChange.emit(this.widgetExportData);
        sessionStorage.removeItem('ea-oidc');
        this.tokenService.updateToken(exportData.token);
        if (!exportData.token) {
            if (typeof exportData.statusMsg === 'string' && exportData.statusMsg.toLowerCase().startsWith('new')) {
                this.loginService.setupAlert({
                    type: 'success',
                    heading: this.dictionaryPipe.transform('ea-authenticate/loginSuccess/heading'),
                    message: this.dictionaryPipe.transform('ea-authenticate/loginSuccess/detail')
                });
                // Remove code & state from url
                this.location.go('/');
                this.loginService.loginStep = 'reset';
                setTimeout(() => {
                    this.loginService.loginStep = this.startPageStep;
                });
            }
        }
    }
    handleUnknownError(testMode = false) {
        // Remove code & state from url
        if (!testMode) {
            this.location.go('/');
        }
        sessionStorage.removeItem('ea-oidc');
        this.loginService.setupAlert({
            type: 'error',
            message: this.dictionaryPipe.transform('ea-authenticate/errors/unknownError/detail'),
            heading: this.dictionaryPipe.transform('ea-authenticate/errors/unknownError/title')
        });
        this.loginService.loginStep = 'reset';
        setTimeout(() => {
            this.loginService.loginStep = this.startPageStep;
        });
    }
    handleCatastrophicError(testMode = false) {
        // Remove code & state from url
        if (!testMode) {
            this.location.go('/');
        }
        this.loginService.setupAlert({
            type: 'error',
            message: this.dictionaryPipe.transform('ea-authenticate/errors/catastrophicError/detail'),
            heading: this.dictionaryPipe.transform('ea-authenticate/errors/catastrophicError/title')
        });
        this.loginService.loginStep = 'reset';
        setTimeout(() => {
            this.loginService.loginStep = this.startPageStep;
        });
    }
    /**
     * Export other data from widget. If agnostic application, we're dealing in strings and capture a custom event.
     * @param exportData - the data object to emit to the host app
     */
    widgetStateExportData(exportData) {
        if (this.loginService.appIsAgnostic) {
            this.widgetInfoStateChange.emit(JSON.stringify(exportData));
        }
        else {
            this.widgetInfoStateChange.emit(exportData);
        }
    }
    /**
     * Initialize the dictionary service, if needed
     */
    setUpEADictionaryService() {
        if (typeof this.widgetContextData.tokenReplacements === 'object') {
            this.dictionary.tokenReplacements = this.widgetContextData.tokenReplacements;
        }
        if (this.widgetContextData.config.dictionaryPath && this.widgetContextData.isAgnosticMode) {
            this.dictionary.path = '';
            this.dictionary.file = this.widgetContextData.config.dictionaryPath;
            this.dictionary.load();
            const subscription = this.dictionary.onLoad.subscribe((res) => {
                if (subscription) {
                    subscription.unsubscribe();
                }
            });
        }
    }
    /**
     * Method to load the load spinner overlay with optional message
     * @param data - the options object
     */
    loadSpinnerHandler(data) {
        this.loadingClass = data.loading ? 'loading' : '';
        this.loadSpinnerMsg = data.msg ? data.msg + '...' : '';
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }
    }
    cleanUpForRedirect() {
        this.loadSpinnerHandler({
            loading: false
        });
        this.loginService.loginStep = this.startPageStep;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateComponent, deps: [{ token: OidLoginDataService }, { token: i1.TokenService }, { token: EaWidgetContextService }, { token: i1.DictionaryService }, { token: i0.ChangeDetectorRef }, { token: i5.ActivatedRoute }, { token: i6.Location }, { token: OIDCService }, { token: i1.DictionaryPipe }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: EaAuthenticateComponent, selector: "cudc-ea-authenticate", inputs: { widgetContext: "widgetContext", validOverride: "validOverride" }, outputs: { widgetExportDataChange: "widgetExportDataChange", widgetInfoStateChange: "widgetInfoStateChange" }, providers: [DictionaryPipe], usesOnChanges: true, ngImport: i0, template: "<div\n  id=\"login-container\"\n  class=\"bl-border-radius--16 bl-overflow--hidden\"\n  [attr.ea-widget-name]=\"widgetName\"\n  [attr.ea-widget-version]=\"widgetVersion\"\n>\n  <div\n    id=\"login-window\"\n    [ngClass]=\"loadingClass\"\n    class=\"bl-bg-color--white bl-border-radius--16 bl-border bl-padding--16 bl-padding-sm--24\"\n  >\n    <oid-login\n      *ngIf=\"loginService.getLoginInfo('widgetMode') !== 'create' && widgetStartStates.indexOf(loginService.loginStep) !== -1\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n    ></oid-login>\n\n    <oid-create-user\n      *ngIf=\"loginService.getLoginInfo('widgetMode') === 'create' && loginService.loginStep === 'oid-start'\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [deferredCreationState]=\"defferedCreationState\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n      [validOverride]=\"validOverride\"\n    >\n      <ng-content></ng-content>\n    </oid-create-user>\n\n    <oid-verify\n      *ngIf=\"loginService.loginStep === 'oid-verify'\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n    ></oid-verify>\n  </div>\n  <p *ngIf=\"loadSpinnerMsg\" class=\"loadSpinnerText\">{{ loadSpinnerMsg }}</p>\n</div>\n", styles: [":host{max-width:430px;min-width:280px;display:block}#login-container{position:relative;display:block;min-height:200px}#login-window{min-height:inherit}#login-window:before,#login-window:after{display:block;content:\" \";width:100%;height:100%;position:absolute;left:0;top:0;background-color:transparent;transition:.4s all ease-out 0s;z-index:-1;border-radius:16px}#login-window:after{background-color:transparent;background-image:url(\"data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64' width='64' height='64'%3E%3Cg class='nc-icon-wrapper' fill='%23445663'%3E%3Cg class='nc-loop_circle-02-64' transform='rotate(116.08357142806719 32 32)'%3E%3Cpath opacity='0.4' fill='%23445663' d='M32,64C14.35498,64,0,49.64453,0,32C0,14.35498,14.35498,0,32,0s32,14.35498,32,32 C64,49.64453,49.64502,64,32,64z M32,6C17.66357,6,6,17.66357,6,32c0,14.33691,11.66357,26,26,26s26-11.66309,26-26 C58,17.66357,46.33643,6,32,6z'/%3E%3Cpath data-color='color-2' d='M64,32h-6C58,17.66357,46.33643,6,32,6V0C49.64502,0,64,14.35498,64,32z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-size:40px;background-repeat:no-repeat;background-position:center center;animation:rotating 1s linear infinite;height:40px;width:40px;left:calc(50% - 20px);top:calc(50% - 20px);opacity:0}#login-window.loading:before,#login-window.loading:after{background-color:#0000004d;z-index:1}#login-window.loading:after{background-color:transparent;opacity:1}p.loadSpinnerText{position:absolute;top:calc(50% - 80px);left:50%;transform:translate(-50%);background:#1c2b36;color:#fff;padding:10px 20px;border-radius:10px;z-index:1000;width:fit-content;max-width:90%}\n"], dependencies: [{ kind: "directive", type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: OidVerifyCodeComponent, selector: "oid-verify", inputs: ["shouldShowAlert"], outputs: ["loadSpinner"] }, { kind: "component", type: OidLoginComponent, selector: "oid-login", inputs: ["shouldShowAlert"], outputs: ["loadSpinner"] }, { kind: "component", type: OidCreateUserComponent, selector: "oid-create-user", inputs: ["deferredCreationState", "shouldShowAlert", "validOverride"], outputs: ["loadSpinner"] }], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cudc-ea-authenticate', providers: [DictionaryPipe], template: "<div\n  id=\"login-container\"\n  class=\"bl-border-radius--16 bl-overflow--hidden\"\n  [attr.ea-widget-name]=\"widgetName\"\n  [attr.ea-widget-version]=\"widgetVersion\"\n>\n  <div\n    id=\"login-window\"\n    [ngClass]=\"loadingClass\"\n    class=\"bl-bg-color--white bl-border-radius--16 bl-border bl-padding--16 bl-padding-sm--24\"\n  >\n    <oid-login\n      *ngIf=\"loginService.getLoginInfo('widgetMode') !== 'create' && widgetStartStates.indexOf(loginService.loginStep) !== -1\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n    ></oid-login>\n\n    <oid-create-user\n      *ngIf=\"loginService.getLoginInfo('widgetMode') === 'create' && loginService.loginStep === 'oid-start'\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [deferredCreationState]=\"defferedCreationState\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n      [validOverride]=\"validOverride\"\n    >\n      <ng-content></ng-content>\n    </oid-create-user>\n\n    <oid-verify\n      *ngIf=\"loginService.loginStep === 'oid-verify'\"\n      (loadSpinner)=\"loadSpinnerHandler($event)\"\n      [shouldShowAlert]=\"shouldShowAlert\"\n    ></oid-verify>\n  </div>\n  <p *ngIf=\"loadSpinnerMsg\" class=\"loadSpinnerText\">{{ loadSpinnerMsg }}</p>\n</div>\n", styles: [":host{max-width:430px;min-width:280px;display:block}#login-container{position:relative;display:block;min-height:200px}#login-window{min-height:inherit}#login-window:before,#login-window:after{display:block;content:\" \";width:100%;height:100%;position:absolute;left:0;top:0;background-color:transparent;transition:.4s all ease-out 0s;z-index:-1;border-radius:16px}#login-window:after{background-color:transparent;background-image:url(\"data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64' width='64' height='64'%3E%3Cg class='nc-icon-wrapper' fill='%23445663'%3E%3Cg class='nc-loop_circle-02-64' transform='rotate(116.08357142806719 32 32)'%3E%3Cpath opacity='0.4' fill='%23445663' d='M32,64C14.35498,64,0,49.64453,0,32C0,14.35498,14.35498,0,32,0s32,14.35498,32,32 C64,49.64453,49.64502,64,32,64z M32,6C17.66357,6,6,17.66357,6,32c0,14.33691,11.66357,26,26,26s26-11.66309,26-26 C58,17.66357,46.33643,6,32,6z'/%3E%3Cpath data-color='color-2' d='M64,32h-6C58,17.66357,46.33643,6,32,6V0C49.64502,0,64,14.35498,64,32z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-size:40px;background-repeat:no-repeat;background-position:center center;animation:rotating 1s linear infinite;height:40px;width:40px;left:calc(50% - 20px);top:calc(50% - 20px);opacity:0}#login-window.loading:before,#login-window.loading:after{background-color:#0000004d;z-index:1}#login-window.loading:after{background-color:transparent;opacity:1}p.loadSpinnerText{position:absolute;top:calc(50% - 80px);left:50%;transform:translate(-50%);background:#1c2b36;color:#fff;padding:10px 20px;border-radius:10px;z-index:1000;width:fit-content;max-width:90%}\n"] }]
        }], ctorParameters: () => [{ type: OidLoginDataService }, { type: i1.TokenService }, { type: EaWidgetContextService }, { type: i1.DictionaryService }, { type: i0.ChangeDetectorRef }, { type: i5.ActivatedRoute }, { type: i6.Location }, { type: OIDCService }, { type: i1.DictionaryPipe }], propDecorators: { widgetContext: [{
                type: Input
            }], validOverride: [{
                type: Input
            }], widgetExportDataChange: [{
                type: Output
            }], widgetInfoStateChange: [{
                type: Output
            }] } });

class PwRequirementsComponent {
    constructor() {
        this.pwValid = new EventEmitter();
        this.pwMatch = new EventEmitter();
        // These are the security teams minimum standards.
        this._defaultRequirements = {
            minLength: 8,
            maxLength: 100,
            requireLetter: true,
            requireNumber: true,
            requireSymbol: true,
            allowedSymbols: [
                '\\s',
                '~',
                '`',
                '!',
                '@',
                '#',
                '$',
                '%',
                '\\',
                '^',
                '&',
                '*',
                '+',
                '=',
                '\\-',
                '\\[',
                '\\]',
                '\\\\',
                "'",
                ';',
                ',',
                '/',
                '{',
                '}',
                '|',
                '\\\\"',
                ':',
                '<',
                '>',
                '\\?',
                '(',
                ')',
                '\\.',
                '_'
            ],
            requireUppercase: true,
            requireLowercase: true
        };
        this.results = { isValid: false };
        this.isMatch = false;
    }
    ngOnInit() {
        if (!this.requirements || (this.requirements.hasOwnProperty('ok') && this.requirements['ok'] === false)) {
            this.requirements = JSON.parse(JSON.stringify(this._defaultRequirements));
        }
        this.buildSymbolRegex();
    }
    ngOnChanges(changes) {
        this.checkPasswordRequirements();
    }
    /**
     * Method to construct regex based on the allowed symbols for pw validation.
     * @param symbolArray Array of individual symbols to be allowed.
     */
    buildSymbolRegex() {
        let symbolArray = JSON.parse(JSON.stringify(this.requirements.allowedSymbols));
        // If no symbol array, use defaults
        if (!symbolArray || symbolArray.length === 0) {
            symbolArray = JSON.parse(JSON.stringify(this._defaultRequirements.allowedSymbols));
        }
        // Combine all symbols into one string array. This assumes any characters that need escaping are already done.
        let regexString = '[' + symbolArray.join('') + ']';
        this.symbolRegex = new RegExp(regexString, 'g');
    }
    /**
     * Checks the pw argument against the enabled pw requirements.
     * @param pw The password to check
     * @returns PWCheck obj with booleans for each requirement that is enabled, and isValid for the aggregation of those requirements.
     */
    checkPasswordRequirements() {
        let results = { isValid: false };
        if (!this.requirements) {
            // get them?
            return;
        }
        if (typeof this.password === 'undefined')
            return;
        if (this.requirements.maxLength) {
            results.maxLength = this.password.length <= this.requirements.maxLength;
        }
        if (this.requirements.minLength) {
            results.minLength = this.password.length >= this.requirements.minLength;
        }
        if (this.requirements.requireLetter && !this.requirements.requireUppercase && !this.requirements.requireLowercase) {
            results.requireLetter = /[A-Za-z]/g.test(this.password);
        }
        if (this.requirements.requireLowercase) {
            results.requireLowercase = /[a-z]/g.test(this.password);
        }
        if (this.requirements.requireUppercase) {
            results.requireUppercase = /[A-Z]/g.test(this.password);
        }
        if (this.requirements.requireNumber) {
            results.requireNumber = /[0-9]/g.test(this.password);
        }
        if (this.requirements.requireSymbol) {
            this.symbolRegex.lastIndex = 0;
            results.requireSymbol = this.symbolRegex.test(this.password);
        }
        if (this.matchPw) {
            this.isMatch = this.checkPwMatch(this.password, this.passwordVerify);
            this.pwMatch.emit(this.isMatch);
        }
        // Check the existing requirements. If all true, set isValid to true.
        let pwValid = true;
        for (const prop in results) {
            if (prop !== 'isValid' && results[prop] === false) {
                pwValid = false;
                break;
            }
        }
        results.isValid = pwValid;
        this.results = results;
        this.pwValid.emit(results.isValid);
    }
    /**
     * Checks if both passwords are the same.
     * @param pw1 The password
     * @param pw2 The password confirmation
     * @returns Boolean
     */
    checkPwMatch(pw1, pw2) {
        if (!pw1 || !pw2) {
            return false;
        }
        return pw1 === pw2;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PwRequirementsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PwRequirementsComponent, selector: "pw-requirements", inputs: { requirements: "requirements", password: "password", matchPw: "matchPw", passwordVerify: "passwordVerify" }, outputs: { pwValid: "pwValid", pwMatch: "pwMatch" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"bl-screen-reader-only\" id=\"accessible-pw-reqs\" aria-live=\"polite\" aria-atomic=\"true\">\n  <div *ngIf=\"matchPw; else pwInvalid\">\n    <p [innerText]=\"results.isValid && isMatch ? 'All password requirements met' : 'Unsatisfied password requirements'\"></p>\n  </div>\n\n  <ng-template #pwInvalid>\n    <p [innerText]=\"results.isValid ? 'All password requirements met' : 'Unsatisfied password requirements'\"></p\n  ></ng-template>\n\n  <ul>\n    <li *ngIf=\"requirements?.minLength && (!results.minLength || !results.maxLength)\">\n      {{ 'ea-authenticate-pw-requirements/length-prefix' | dictionary }}\n      {{ requirements.minLength }}\n      {{ 'ea-authenticate-pw-requirements/length-and' | dictionary }}\n      {{ requirements.maxLength }}\n      {{ 'ea-authenticate-pw-requirements/length-suffix' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireLetter && !requirements.requireLowercase && !requirements.requireUppercase && !results.requireLetter\">\n      {{ 'ea-authenticate-pw-requirements/letters' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireLowercase && !results.requireLowercase\">\n      {{ 'ea-authenticate-pw-requirements/lowercase' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireUppercase && !results.requireUppercase\">\n      {{ 'ea-authenticate-pw-requirements/uppercase' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireNumber && !results.requireNumber\">\n      Contains at least\n      {{ 'ea-authenticate-pw-requirements/number' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireSymbol && !results.requireSymbol\">\n      {{ 'ea-authenticate-pw-requirements/special' | dictionary }}\n    </li>\n    <li *ngIf=\"this.matchPw && !isMatch\">\n      {{ 'ea-authenticate-pw-requirements/match' | dictionary }}\n    </li>\n  </ul>\n</div>\n<div class=\"pw-requirements-wrapper\">\n  <div class=\"bl-margin-top--16 password-requirement\">\n    {{ 'ea-authenticate-pw-requirements/requirements' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.minLength\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.minLength && results.maxLength ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/length-prefix' | dictionary }}\n    {{ requirements.minLength }}\n    {{ 'ea-authenticate-pw-requirements/length-and' | dictionary }}\n    {{ requirements.maxLength }}\n    {{ 'ea-authenticate-pw-requirements/length-suffix' | dictionary }}\n  </div>\n  <div\n    *ngIf=\"requirements?.requireLetter && !requirements.requireLowercase && !requirements.requireUppercase\"\n    class=\"bl-margin-top--4 password-requirement\"\n  >\n    <i [ngClass]=\"results.requireLetter ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/letters' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireLowercase\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireLowercase ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/lowercase' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireUppercase\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireUppercase ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/uppercase' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireNumber\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireNumber ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/number' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireSymbol\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireSymbol ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/special' | dictionary }}\n  </div>\n  <div *ngIf=\"this.matchPw\" class=\"bl-margin-top--4 bl-margin-bottom--32 password-requirement\">\n    <i [ngClass]=\"isMatch ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/match' | dictionary }}\n  </div>\n</div>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}.password-requirement{font-size:16px;line-height:26px}\n"], dependencies: [{ kind: "directive", type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.DictionaryPipe, name: "dictionary" }], preserveWhitespaces: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PwRequirementsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pw-requirements', template: "<div class=\"bl-screen-reader-only\" id=\"accessible-pw-reqs\" aria-live=\"polite\" aria-atomic=\"true\">\n  <div *ngIf=\"matchPw; else pwInvalid\">\n    <p [innerText]=\"results.isValid && isMatch ? 'All password requirements met' : 'Unsatisfied password requirements'\"></p>\n  </div>\n\n  <ng-template #pwInvalid>\n    <p [innerText]=\"results.isValid ? 'All password requirements met' : 'Unsatisfied password requirements'\"></p\n  ></ng-template>\n\n  <ul>\n    <li *ngIf=\"requirements?.minLength && (!results.minLength || !results.maxLength)\">\n      {{ 'ea-authenticate-pw-requirements/length-prefix' | dictionary }}\n      {{ requirements.minLength }}\n      {{ 'ea-authenticate-pw-requirements/length-and' | dictionary }}\n      {{ requirements.maxLength }}\n      {{ 'ea-authenticate-pw-requirements/length-suffix' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireLetter && !requirements.requireLowercase && !requirements.requireUppercase && !results.requireLetter\">\n      {{ 'ea-authenticate-pw-requirements/letters' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireLowercase && !results.requireLowercase\">\n      {{ 'ea-authenticate-pw-requirements/lowercase' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireUppercase && !results.requireUppercase\">\n      {{ 'ea-authenticate-pw-requirements/uppercase' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireNumber && !results.requireNumber\">\n      Contains at least\n      {{ 'ea-authenticate-pw-requirements/number' | dictionary }}\n    </li>\n    <li *ngIf=\"requirements?.requireSymbol && !results.requireSymbol\">\n      {{ 'ea-authenticate-pw-requirements/special' | dictionary }}\n    </li>\n    <li *ngIf=\"this.matchPw && !isMatch\">\n      {{ 'ea-authenticate-pw-requirements/match' | dictionary }}\n    </li>\n  </ul>\n</div>\n<div class=\"pw-requirements-wrapper\">\n  <div class=\"bl-margin-top--16 password-requirement\">\n    {{ 'ea-authenticate-pw-requirements/requirements' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.minLength\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.minLength && results.maxLength ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/length-prefix' | dictionary }}\n    {{ requirements.minLength }}\n    {{ 'ea-authenticate-pw-requirements/length-and' | dictionary }}\n    {{ requirements.maxLength }}\n    {{ 'ea-authenticate-pw-requirements/length-suffix' | dictionary }}\n  </div>\n  <div\n    *ngIf=\"requirements?.requireLetter && !requirements.requireLowercase && !requirements.requireUppercase\"\n    class=\"bl-margin-top--4 password-requirement\"\n  >\n    <i [ngClass]=\"results.requireLetter ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/letters' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireLowercase\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireLowercase ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/lowercase' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireUppercase\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireUppercase ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/uppercase' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireNumber\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireNumber ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/number' | dictionary }}\n  </div>\n  <div *ngIf=\"requirements?.requireSymbol\" class=\"bl-margin-top--4 password-requirement\">\n    <i [ngClass]=\"results.requireSymbol ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/special' | dictionary }}\n  </div>\n  <div *ngIf=\"this.matchPw\" class=\"bl-margin-top--4 bl-margin-bottom--32 password-requirement\">\n    <i [ngClass]=\"isMatch ? 'valid-pw cu-check-circle' : 'invalid-pw cu-error-circle'\"></i>\n    {{ 'ea-authenticate-pw-requirements/match' | dictionary }}\n  </div>\n</div>\n", styles: [".ea-login-max-width{max-width:364px}.ea-login-primary-color{color:#445663}.form-group .bl-input.input-override{font-size:16px;line-height:26px}.form-group .password-visibility{position:absolute;top:0;right:0;outline:none}.form-group .password-visibility i{border:1px dotted transparent}.form-group .password-visibility:focus i{border-color:#68788e}.form-group .password-input-container input::-ms-clear,.form-group .password-input-container input::-ms-reveal{display:none}.valid-pw{color:#008062}.invalid-pw{color:#a5b0b8}a:not([href]){text-decoration:underline!important;cursor:pointer}.password-requirement{font-size:16px;line-height:26px}\n"] }]
        }], ctorParameters: () => [], propDecorators: { requirements: [{
                type: Input
            }], password: [{
                type: Input
            }], matchPw: [{
                type: Input
            }], passwordVerify: [{
                type: Input
            }], pwValid: [{
                type: Output
            }], pwMatch: [{
                type: Output
            }] } });

class PhoneMaskPipe {
    transform(value, backspace) {
        if (!value) {
            return '';
        }
        let newVal = value.replace(/\D/g, '');
        if (backspace && newVal.length <= 6) {
            newVal = newVal.substring(0, newVal.length - 1);
        }
        if (newVal.length === 0) {
            newVal = '';
        }
        else if (newVal.length < 3) {
            newVal = newVal.replace(/^(\d{0,3})/, '($1');
        }
        else if (newVal.length === 3) {
            newVal = newVal.replace(/^(\d{0,3})/, '($1)');
        }
        else if (newVal.length <= 6) {
            newVal = newVal.replace(/^(\d{0,3})(\d{0,3})/, '($1) $2');
        }
        else if (newVal.length <= 10) {
            newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
        }
        else {
            // If number is international, just strip non digits.
            newVal = newVal.replace(/\D/g, '');
        }
        return newVal;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PhoneMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: PhoneMaskPipe, name: "phoneMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PhoneMaskPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'phoneMask'
                }]
        }] });

class EaAuthenticateModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateModule, declarations: [EaAuthenticateComponent,
            NumericOnlyDirective,
            AlphaNumericOnlyDirective,
            EllipsesPipe,
            PwRequirementsComponent,
            LoginBtnComponent,
            SeparatorComponent,
            PhoneMaskPipe,
            OidVerifyCodeComponent,
            OidLoginComponent,
            OidCreateUserComponent], imports: [CommonModule,
            SharedUiLibraryModule,
            FormsModule, i6$1.CodeInputModule, NgxMaskDirective,
            NgxMaskPipe], exports: [EaAuthenticateComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateModule, providers: [provideNgxMask()], imports: [CommonModule,
            SharedUiLibraryModule,
            FormsModule,
            CodeInputModule.forRoot({
                codeLength: 6,
                isCharsCode: false
            })] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EaAuthenticateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        EaAuthenticateComponent,
                        NumericOnlyDirective,
                        AlphaNumericOnlyDirective,
                        EllipsesPipe,
                        PwRequirementsComponent,
                        LoginBtnComponent,
                        SeparatorComponent,
                        PhoneMaskPipe,
                        OidVerifyCodeComponent,
                        OidLoginComponent,
                        OidCreateUserComponent
                    ],
                    imports: [
                        CommonModule,
                        SharedUiLibraryModule,
                        FormsModule,
                        CodeInputModule.forRoot({
                            codeLength: 6,
                            isCharsCode: false
                        }),
                        NgxMaskDirective,
                        NgxMaskPipe
                    ],
                    exports: [EaAuthenticateComponent],
                    providers: [provideNgxMask()]
                }]
        }] });

/*
 * Public API Surface of ea-login
 */

/**
 * Generated bundle index. Do not edit.
 */

export { EaAuthenticateComponent, EaAuthenticateModule, OidLoginDataService };
